import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { SuperTableColumnWidth } from '@modeso/aumico-lib-notes-fe/enums/SuperTableColumnWidth.ebum';
import {
    ISuperTableCalculation,
    ISuperTableCalculationAxis,
    ISuperTableCalculationFunction,
    ISuperTableComponent,
    ISuperTableReconcileItem,
    ISuperTableRow,
    ItemType,
    SuperTableRowType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { AumicoStyleOptions, ICellFormat, IDynamicKeyValue, ViewMode } from '@modeso/types__aumico-lib-core-be';
import {
    ISuperTableOptions,
    ISuperTableSegment,
    IXAxisConfigColumn,
    SuperTableColumnType,
} from '@modeso/types__aumico-lib-notes-be';
import { SuperTableColumnBelongingYear } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/super_table_column_belonging_year.enum';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { debounceTime, distinctUntilChanged, filter, pairwise, startWith } from 'rxjs';
import { DEFAULT_SWISS_DISPLAY_OPTIONS } from '../../../../../aumico-lib-annualreport-fe/src/lib';
import {
    EMPTY_ARRAY_LENGTH,
    FIRST_ELEMENT_INDEX,
    NumberUtils,
    SECOND_ELEMENT_INDEX,
    TWO_DECIMAL_PLACES,
    USER_INPUT_DEBOUNCE_TIME,
} from '../../../../../aumico-lib-core-fe/src/lib';
import { SuperTableAddColumn } from '../../enums/super-table-add-column.enum';
import { SuperTableAddRow } from '../../enums/super-table-add-row.enum';
import { SuperTableHelper } from '../../helpers/super-table.helper';
import { IAnnualReportDefaultSettings } from '../../interfaces/IAnnualReportDefaultSettings.interface';
import { FX_RESERVE_NODE_NAME, IFxNodeStamp, IFxNotesContext } from '../../interfaces/IFxNotesContext.interface';
import { MappingItemsSelectionNode } from '../../interfaces/mapping-items-selection-node.interface';
import { INotesSuperTableDataForPrintPreview } from '../../interfaces/notes-super-table-data-for-print-preview.interface';
import { ISuperTableAddCalculationResult } from '../../interfaces/super-table-add-calculation-result.interface';
import { ISuperTableAddFreeColumnDialogResult } from '../../interfaces/super-table-add-free-column-dialog-result.interface';
import { ISuperTableComponentFormValue } from '../../interfaces/super-table-component-form-value.interface';
import { ISuperTableComponentRowFormValue } from '../../interfaces/super-table-component-row-form-value.interface';
import { ISuperTableCreateSegmentDialogResult } from '../../interfaces/super-table-create-segment-dialog-result.interface';
import { ISuperTableItemSelection } from '../../interfaces/super-table-item-selection.interface';
import { SuperTableMappingRowSettings } from '../../interfaces/super-table-mapping-row-settings.interface';
import { NotesDisplayMapper } from '../../mappers/notes-display.mapper';
import { SuperTableMapper } from '../../mappers/super-table.mapper';
import { NotesPageBase } from '../NotesPageBase';
import { ISuperTableDelegate } from './ISuperTableDelegate';
import { SelectMappingDialogComponent } from './select-mapping-dialog';

// Extended interface for calculations with invalidRef flag
interface ISuperTableCalculationWithInvalidRef extends ISuperTableCalculation {
    invalidRef?: boolean;
}

const debug = Debug('aumico:aumico-lib-notes-fe:SuperTableComponent');

const MAX_COLUMN_NUMBER = 7;
const MAX_ROW_NUMBER = 7;
const MIN_DECIMAL = 0;
const MAX_DECIMAL = 5;

@UntilDestroy()
@Component({
    selector: 'aumico-notes-super-table',
    templateUrl: 'super-table.component.html',
    styleUrls: ['super-table.component.scss'],
})
export class SuperTableComponent extends NotesPageBase implements OnInit, OnChanges {
    private autoScrollInterval: any = null;
    private lastDragEvent: any = null;

    // Access the native elements for the trigger and the menu
    @ViewChild('menuTrigger') menuTriggerRef!: ElementRef;
    @ViewChild('menuContainer', { static: false, read: ElementRef }) menuContainerRef!: ElementRef;

    ViewMode = ViewMode;

    /**
     * REV-20899: combine the imported balance with the aggregated transactional
     * reclass so super-table sums and the mapping-tree display always show the
     * "final" (reclass-applied) value. `closingReClass` is populated:
     *  - on leaf accounts by `applyReclassificationsToFinStatements` (provider boundary)
     *  - on every FLI / SubFLI / group / section by `GroupingHelper.recalculateGroupSummaries`
     * Tolerates missing / non-finite reclasses by treating them as zero.
     */
    private finalCYValue(data: { calculatedClosingBalance?: number | null; closingReClass?: number | null } | null | undefined): number | undefined {
        if (data == null) return undefined;
        const balance = data.calculatedClosingBalance;
        const reclass = data.closingReClass;
        if (balance == null && (reclass == null || reclass === 0)) return balance ?? undefined;
        return (balance ?? 0) + (reclass ?? 0);
    }

    private finalPYValue(data: { calculatedOpeningBalance?: number | null; openingReClass?: number | null } | null | undefined): number | undefined {
        if (data == null) return undefined;
        const balance = data.calculatedOpeningBalance;
        const reclass = data.openingReClass;
        if (balance == null && (reclass == null || reclass === 0)) return balance ?? undefined;
        return (balance ?? 0) + (reclass ?? 0);
    }

    @Input() language: string = 'de-ch';
    @Input() componentIndex: number;
    @Input()
    private superTableSavedData: ISuperTableComponent | undefined;
    private _componentOptions: ISuperTableOptions;
    columnToRemove: IXAxisConfigColumn;
    showColumnDeletionDialog: boolean;
    rowToRemoveIndex: number;
    showRowDeletionDialog: boolean;
    columnOptions: { id: string; name: string }[];
    showAddNewSegementDialog: boolean;
    segementFormValue: ISuperTableCreateSegmentDialogResult;
    freeColumnFormValue: ISuperTableAddFreeColumnDialogResult;
    showAddFreeColumnDialog: boolean;
    freeColumnFormIsValid: boolean;
    segmentToEdit: ISuperTableSegment | null = null;
    columnToEditIndex: number;
    disableOutsideClick: boolean;
    hideCurrentYear: boolean = false;
    showFiscalYear: boolean = false;
    footnotes: string[] = [];
    private currentFootnoteValue: string = '';

    @Input() set componentData(value: ISuperTableComponent | undefined) {
        if (value == null) {
            return;
        }
        this.superTableSavedData = cloneDeep<ISuperTableComponent>(value);
        this.title = this.superTableSavedData.options.title;
        this.xAxisConfigCols = cloneDeep<IXAxisConfigColumn[]>(this.superTableSavedData.options.xAxisConfig);
        this.setFormattingForColumns();
        this.segments = cloneDeep<ISuperTableSegment[]>(this.superTableSavedData.options.segments);
        this.calculations = cloneDeep<ISuperTableCalculationWithInvalidRef[]>(this.superTableSavedData.calculations);
        this.compareYears = this.superTableSavedData.options.compareYears;
        this.hideCurrentYear = this.superTableSavedData.options.hideCurrentYear
            ? this.superTableSavedData.options.hideCurrentYear
            : false;
        this.showFiscalYear = this.superTableSavedData.options.showFiscalYear
            ? this.superTableSavedData.options.showFiscalYear
            : false;
        this.transpose = this.superTableSavedData.options.transpose
            ? this.superTableSavedData.options.transpose
            : false;
        this.showPreviousYear = this.superTableSavedData.options.showPreviousYear
            ? this.superTableSavedData.options.showPreviousYear
            : false;
        this.showCurrency = this.superTableSavedData.options.showCurrency
            ? this.superTableSavedData.options.showCurrency
            : false;
        // FX (REV-21512): absent means the FX default (all four year columns visible).
        this.fxHideCurrentYear = this.superTableSavedData.options.fxHideCurrentYear === true;
        this.fxHidePreviousYear = this.superTableSavedData.options.fxHidePreviousYear === true;
        this.footnotes = this.superTableSavedData.footnotes || [];
        if (this.footnotes.length > 0) {
            this.currentFootnoteValue = this.footnotes[0];
        }
        this.prepareStartingColumnIdToSegmentMap();
        this.initializeFormWithSavedData(this.superTableSavedData);
        this.handleCompareYears();
        this.handleHideCurrentYear();
        this.ensureFxColumns();
    }

    currentYearDate = new Date().toDateString();
    previousYearDate = new Date().toDateString();
    // only use in notes section, not on print page
    prepreviousYearDate = new Date().toDateString();
    currency = 'CHF';

    @Input() set annualReportDefaultSettings(value: IAnnualReportDefaultSettings) {
        this.currentYearDate = value.currentBalanceDate;
        this.previousYearDate = value.previousBalanceDate; // Create a Date instance from previousYearDate.
        // Calculate the prepreviousYearDate by subtracting 1 year.
        // Store the result in ISO string format, or assign directly as a Date.
        // We can do that because that Date is only displayed in the notes section. not in print.
        // In print it will always take the correct values because we display the print super table component with each section data of each year.
        if (value.previousBalanceDate != null && value.previousBalanceDate !== '-') {
            const previousDate = new Date(value.previousBalanceDate);
            previousDate.setFullYear(previousDate.getFullYear() - 1);

            this.prepreviousYearDate = previousDate.toISOString();
        }

        this.currency = value.currency;
        if (this._previousYearPreviewDataValue) {
            this.previousYearPreviewData = this.notesDisplayMapper.mapNotesSuperTableToPrintPreviewData(
                this._previousYearPreviewDataValue,
                this.previousYearDate,
                this.prepreviousYearDate,
                DEFAULT_SWISS_DISPLAY_OPTIONS,
                undefined,
                this.language,
                this.hasFootnotes,
            );
        }
        if (this.form != null) {
            this.handleFormValueChange(false);
        }
    }

    @Input()
    set componentOptions(value: ISuperTableOptions | undefined) {
        if (value == null || this.superTableSavedData != null) {
            return;
        }
        this._componentOptions = value;
        this.title = value.title;
        this.xAxisConfigCols = cloneDeep<IXAxisConfigColumn[]>(value.xAxisConfig);
        this.segments = cloneDeep<ISuperTableSegment[]>(value.segments);
        this.compareYears = value.compareYears;
        this.hideCurrentYear = value.hideCurrentYear ? value.hideCurrentYear : false;
        this.showFiscalYear = value.showFiscalYear ? value.showFiscalYear : false;
        this.transpose = value.transpose ? value.transpose : false;
        this.prepareStartingColumnIdToSegmentMap();
        this.initializeForm();
        this.handleCompareYears();
        this.ensureFxColumns();
    }

    get componentOptions(): ISuperTableOptions {
        return this._componentOptions;
    }

    @Input() set resetValuesFromMappingTriggered(value: boolean) {
        if (!value) {
            return;
        }
        this.resetFormValuesFromMappingItems();
    }

    transpose = false;
    flipSign = false;
    compareYears = true;
    showPreviousYear = false;
    pageBreak = false;
    showCurrency = false;

    /**
     * FX (REV-21512): null on non-FX reports — no twin columns, no rate rows, exact legacy
     * behaviour (G0). Values are stamped by the app onto the tree nodes (`__fx`); this component
     * only reads. A setter because the context can arrive before or after componentData —
     * `ensureFxColumns()` runs on whichever comes last.
     */
    @Input() set fxNotesContext(value: IFxNotesContext | null) {
        this._fxNotesContext = value;
        this.ensureFxColumns();
        if (this.form != null) {
            this.recomputeFxTwinValues();
        }
    }
    get fxNotesContext(): IFxNotesContext | null {
        return this._fxNotesContext;
    }
    private _fxNotesContext: IFxNotesContext | null = null;
    /** ISuperTableOptions mirrors — the twins' `hideCurrentYear`/`compareYears` counterparts. */
    fxHideCurrentYear = false;
    fxHidePreviousYear = false;
    /** Twin/rate cell values per rowId, re-derived on every form/tree change — never persisted. */
    fxTwinValues = new Map<string, { cy: number | null; py: number | null }>();
    readonly fxRateRowType = SuperTableRowType.RATE;
    /** Set while the mapping picker is open for a position rate row. */
    private pendingRateRowSource: 'position' | null = null;

    /**
     * FX (REV-21512): the display-currency twins are REAL xAxisConfig entries (CY_FX/PV_FX,
     * columnId = enum value) mirroring handleCompareYears/handleHideCurrentYear — created next
     * to their functional original, removed while their hide-flag is set. Metadata only: the
     * cell values stay computed from the stamped trees and are never stored on the rows.
     * No-op without FX context; stray entries from a cross-report copy simply don't render (G0).
     */
    private ensureFxColumns(): void {
        if (this.fxNotesContext == null || this.xAxisConfigCols == null) {
            return;
        }

        // «Währung anzeigen» defaults ON for FX tables (one table-wide setting, all columns) —
        // only while the blob carries no explicit choice yet.
        const savedOptions = this.superTableSavedData?.options ?? this._componentOptions;
        if (savedOptions != null && savedOptions.showCurrency === undefined) {
            this.showCurrency = true;
        }

        this.ensureFxColumn(
            SuperTableColumnType.CURRENT_YEAR_FX,
            SuperTableColumnBelongingYear.CURRENT_YEAR,
            this.fxHideCurrentYear,
            [SuperTableColumnType.CURRENT_YEAR],
        );
        this.ensureFxColumn(
            SuperTableColumnType.PREVIOUS_YEAR_FX,
            SuperTableColumnBelongingYear.PREVIOUS_YEAR,
            this.fxHidePreviousYear,
            [SuperTableColumnType.PREVIOUS_YEAR, SuperTableColumnType.CURRENT_YEAR_FX, SuperTableColumnType.CURRENT_YEAR],
        );
    }

    private ensureFxColumn(
        type: SuperTableColumnType,
        belongingYear: SuperTableColumnBelongingYear,
        hidden: boolean,
        anchorTypes: SuperTableColumnType[],
    ): void {
        const existing = this.xAxisConfigCols.find((col) => col.type === type);
        if (hidden) {
            if (existing != null) {
                this.handleColumnRemoval(existing);
            }
            return;
        }
        if (existing != null) {
            return;
        }

        // Same default format as the CY/PV entries created by handleCompareYears/handleHideCurrentYear.
        const fxColumn: IXAxisConfigColumn = {
            columnId: type,
            type,
            belongingYear,
            format: {
                styles: { [AumicoStyleOptions.RIGHT_ALIGNED]: { enabled: true, appliedAt: new Date() } } as Record<
                    AumicoStyleOptions,
                    { enabled: boolean; appliedAt: Date }
                >,
                numberOfDecimals: {
                    value: TWO_DECIMAL_PLACES,
                },
            },
        };
        const anchorIndex = anchorTypes
            .map((anchorType) => this.xAxisConfigCols.findIndex((col) => col.type === anchorType))
            .find((index) => index >= 0);
        const insertAt = anchorIndex != null ? anchorIndex + 1 : this.xAxisConfigCols.length;
        this.xAxisConfigCols.splice(insertAt, 0, fxColumn);
        this.xAxisConfigCols = [...this.xAxisConfigCols];

        // Existing rows need the twin control for user input — same move as onCreateFreeColumn,
        // seeded from the saved blob in case the FX context arrived after the form was built.
        const rowsFormArray = this.form?.get('rows') as UntypedFormArray | undefined;
        rowsFormArray?.controls.forEach((row) => {
            const rowGroup = row as UntypedFormGroup;
            if (rowGroup.get(type) == null) {
                const savedRow = this.superTableSavedData?.rows?.find(
                    (saved) => saved.rowId === rowGroup.get('rowId')?.value,
                );
                rowGroup.addControl(type, new UntypedFormControl(savedRow?.[type] ?? null));
            }
        });
    }

    @Input() balanceMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;
    @Input() incomeMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;

    allMappingItemsSelectionTree: MappingItemsSelectionNode | undefined;

    private delegateData: ISuperTableDelegate | undefined;
    @Input() set delegate(value: ISuperTableDelegate) {
        this.delegateData = value;
        if (this.form != null) {
            const superTableComponent = this.mapFormValueToSuperTableComponent(this.form.value);

            this.delegateData.superTableComponentData(superTableComponent, this.componentIndex);
        }
    }
    @Input() previewMode = false;
    @Input() viewMode: ViewMode = ViewMode.EDIT;

    @Input() highlightTodos = true;

    @Input() set autofillMode(autofillMode: boolean) {
        if (this.superTableSavedData != null && this.form != null && autofillMode) {
            this.initializeFormWithSavedData(this.superTableSavedData);
        }
    }

    _previousYearPreviewDataValue: ISuperTableComponent | undefined;
    previousYearPreviewData: INotesSuperTableDataForPrintPreview | undefined;
    @Input() set previousYearComponentData(value: ISuperTableComponent | undefined) {
        if (value == null) {
            return;
        }
        this._previousYearPreviewDataValue = value;
        this.previousYearPreviewData = this.notesDisplayMapper.mapNotesSuperTableToPrintPreviewData(
            value,
            this.previousYearDate,
            this.prepreviousYearDate,
            DEFAULT_SWISS_DISPLAY_OPTIONS,
            undefined,
            this.language,
            this.hasFootnotes || false,
        );
    }

    @Input() tableNumber: string | undefined;

    form: UntypedFormGroup;
    title: IDynamicKeyValue | undefined;
    xAxisConfigCols: IXAxisConfigColumn[] = [];
    segments: ISuperTableSegment[] = [];
    startingColumnIdToSegment: Map<string, ISuperTableSegment> = new Map<string, ISuperTableSegment>();
    allColumnIdsUnderASegment: string[];
    calculations: ISuperTableCalculationWithInvalidRef[] = [];
    private lastGivenColumnIndex: number | undefined;
    private lastGivenRowIndex: number | undefined;
    maxColNumber = MAX_COLUMN_NUMBER;
    maxRowNumber = MAX_ROW_NUMBER;

    activeStyleMenuRow: number | null = null;
    activeStyleMenuColumn: number | null = null;

    // Flyout state properties
    isRowContextFlyoutOpen = false;
    isColumnContextFlyoutOpen = false;
    activeContextMenuRow: number | null = null;
    activeContextMenuColumn: number | null = null;
    rowContextFlyoutPosition: { top: number; right: number; left?: number } = { top: 0, right: 0 };
    columnContextFlyoutPosition: { top: number; right: number; left?: number } = { top: 0, right: 0 };

    reconcilePopUpOpened = false;
    openAddNewRowDialog = false;
    openAddNewColumnDialog = false;
    openAddNewRowFromBalance = false;
    openAddNewRowFromIncome = false;
    openAddNewRowFromBalanceOrIncome = false;
    openCalculationFunctionSelection = false;
    /** FX (REV-21512): source picker for a new rate row (closing/average/position). */
    openRateSourceSelection = false;
    openSummationRowDialog = false;
    openSubtractionRowDialog = false;
    openMultiplicationRowDialog = false;
    openRatioRowDialog = false;
    openColumnCalculationFunctionSelection = false;
    openSummationColumnDialog = false;
    openSubtractionColumnDialog = false;
    openMultiplicationColumnDialog = false;
    openRatioColumnDialog = false;

    selectedReconcileItemNodes: MappingItemsSelectionNode[] | undefined;
    currentMappingRowSettings: SuperTableMappingRowSettings | undefined;
    currentSelectedCalculationFunction: ISuperTableCalculationFunction | undefined;
    currentSelectedCalculation: ISuperTableCalculation | undefined;
    rowSelections: ISuperTableItemSelection[] = [];
    columnSelections: IXAxisConfigColumn[] = [];
    currentCalculationOutput: ISuperTableAddCalculationResult | undefined;
    currentSelectedAxis: ISuperTableCalculationAxis | undefined;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private readonly dialog: MatDialog,
        private readonly i18NextService: I18NextService,
        private readonly superTableHelper: SuperTableHelper,
        private readonly superTableMapper: SuperTableMapper,
        private readonly notesDisplayMapper: NotesDisplayMapper,
    ) {
        super();
        this.addSvgIcon('aumico-pattern-icon-remove');
        this.addSvgIcon('aumico-pattern-icon-arrow-left');
        this.addSvgIcon('aumico-pattern-icon-arrow-right');
        this.addSvgIcon('aumico-pattern-icon-move-arrow-vertical');
        this.addSvgIcon('aumico-pattern-icon-edit-notes');
    }

    ngOnInit(): void {
        debug('ngOnInit');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['addNumbering'] || changes['referenceFlis'] || changes['addReferencing']) && this.form != null) {
            this.handleFormValueChange(false);
        }

        if (
            changes['balanceMappingItemsSelectionTree'] ||
            (changes['incomeMappingItemsSelectionTree'] && this.form != null)
        ) {
            // REV-20899: Saved super-table row values were captured the last
            // time the user opened this notes step. When the user adds
            // reclassifications later in grouping & masking, the live
            // mapping trees arrive with refreshed `closingReClass` /
            // `openingReClass` stamped onto every leaf and aggregated up the
            // tree. Re-derive each mapped row's value from the live tree so
            // the editor reflects the current totals — same path the
            // explicit "reset values from mapping" trigger uses, just fired
            // automatically when the trees change. Skip until both trees
            // have arrived because `resetFormValuesFromMappingItems` builds
            // a combined tree for rows without a `financialType`.
            if (
                this.form != null &&
                this.balanceMappingItemsSelectionTree != null &&
                this.incomeMappingItemsSelectionTree != null
            ) {
                this.resetFormValuesFromMappingItems();
            }
            this.recalculateReconcilationMismatchResult();
        }

        if (changes['language'] && changes['language'].currentValue != null && this.form != null) {
            this.form.controls['tableTitle'].setValue(this.title?.[this.language]);
        }
    }

    private initializeForm(): void {
        this.form = new FormGroup({
            rows: this.formBuilder.array([], Validators.required),
            tableTitle: new UntypedFormControl(this.title[this.language]),
            footnote: new UntypedFormControl(this.currentFootnoteValue || ''),
        });

        this.setLastGivenIds();

        this.addFreeTextRow();

        this.handleFormValueChange(false);

        this.form
            .get('rows')
            .valueChanges.pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                startWith(this.form.value.rows),
                pairwise(),
                untilDestroyed(this),
            )
            .subscribe(([previousValue, currentValue]) => {
                const recalculate = this.shouldRecalculate(previousValue, currentValue);

                this.handleFormValueChange(recalculate);
            });

        this.form
            .get('tableTitle')
            .valueChanges.pipe(
                startWith(this.title?.[this.language]),
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                pairwise(),
                untilDestroyed(this),
            )
            .subscribe(([prevValue, currentValue]) => {
                this.handleFormValueChange(false);
            });

        this.form
            .get('footnote')
            .valueChanges.pipe(
                startWith(this.currentFootnoteValue || ''),
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                pairwise(),
                untilDestroyed(this),
            )
            .subscribe(([prevValue, currentValue]) => {
                this.handleFormValueChange(false);
            });

        (this.form.get('rows') as FormArray).controls.forEach((group: FormGroup, index: number) => {
            this.subscribeToRowTitleChanges(group, index);
        });
    }

    private getRowAsFormGroup(row: ISuperTableComponentRowFormValue): UntypedFormGroup {
        const rows = new UntypedFormGroup({
            rowId: new UntypedFormControl(row.rowId != null ? row.rowId : 'row-' + (this.lastGivenRowIndex + 1)),
            title: new UntypedFormControl(row.title),
            type: new UntypedFormControl(row.type),
            itemsIds: new UntypedFormControl(row.itemsIds),
            currentValue: new UntypedFormControl(row.currentValue),
            format: new UntypedFormControl(row.format),
            reconcileItems: new UntypedFormControl(row.reconcileItems),
            financialType: new UntypedFormControl(row.financialType),
            // FX (REV-21512): only set on RATE rows; the rate itself is never stored.
            rateSource: new UntypedFormControl(row.rateSource),
        });

        this.lastGivenRowIndex = this.lastGivenRowIndex + 1;

        this.xAxisConfigCols.forEach((col) => {
            if (col.type === SuperTableColumnType.PREVIOUS_YEAR && this.compareYears) {
                rows.addControl('previousValue', new UntypedFormControl(row.previousValue));
            }
            if (
                col.type === SuperTableColumnType.NUMBER ||
                col.type === SuperTableColumnType.TEXT ||
                col.type === SuperTableColumnType.CALCULATION
            ) {
                let colValue = undefined;
                if (row[col.columnId] != null) {
                    colValue = row[col.columnId];
                }

                rows.addControl(col.columnId, new UntypedFormControl(colValue));
            }
            // FX (REV-21512): twin controls carry ONLY user input (free/title/text rows) — for
            // mapping, rate and calculation rows they stay null and the computed value renders
            // through fxCellControl instead, so computed twins never persist (G2).
            if (SuperTableHelper.isFxColumnType(col.type)) {
                rows.addControl(col.columnId, new UntypedFormControl(row[col.columnId] ?? null));
            }
        });

        return rows;
    }

    private handleFormValueChange(recalculate?: boolean): void {
        if (recalculate == null || recalculate) {
            this.recalculateCalculationRowsAndColumns();
            this.recalculateReconcilationMismatchResult();
        }
        this.recomputeFxTwinValues();

        // Handle footnote changes
        const footnoteValue = this.form.value.footnote;
        if (footnoteValue != null && footnoteValue !== '') {
            this.footnotes = [footnoteValue];
            this.currentFootnoteValue = footnoteValue;
        } else {
            this.footnotes = [];
            this.currentFootnoteValue = '';
        }

        const superTableComponent = this.mapFormValueToSuperTableComponent(this.form.value);

        if (this.delegateData != null) {
            this.delegateData.superTableComponentData(superTableComponent, this.componentIndex);
        }
    }

    private initializeFormWithSavedData(superTableSavedData: ISuperTableComponent): void {
        this.form = new FormGroup({
            rows: this.formBuilder.array([], Validators.required),
            tableTitle: new UntypedFormControl(this.title[this.language]),
            footnote: new UntypedFormControl(''),
        });

        const rowFormGroups: AbstractControl[] = [];
        for (const row of superTableSavedData.rows) {
            const formValueRow: ISuperTableComponentRowFormValue = {
                rowId: row.rowId,
                title: row.title,
                type: row.type,
                itemsIds: row.itemsIds,
                currentValue: row.currentValue,
                // FX (REV-21512): without this, closing/average rate rows lose their source on
                // every (re-)init and fall into the position branch — empty itemsIds ⇒ empty cells.
                rateSource: row.rateSource,
                format: row.format
                    ? this.mapOldFormatToNewFormat(row.format)
                    : {
                        styles: {} as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>,
                        numberOfDecimals: {
                            value: TWO_DECIMAL_PLACES,
                        },
                    },
                reconcileItems: row.reconcileItems,
                financialType: row.financialtype,
            };
            for (const col of this.xAxisConfigCols) {
                if (col.type === SuperTableColumnType.CURRENT_YEAR) {
                    continue;
                }

                if (this.compareYears && col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                    formValueRow.previousValue = row.previousValue;
                }

                formValueRow[col.columnId] = row[col.columnId] as string | number;
            }

            const rowFormGroup = this.getRowAsFormGroup(formValueRow);
            rowFormGroups.push(rowFormGroup);
        }

        const rows = new UntypedFormArray(rowFormGroups);
        this.form.setControl('rows', rows);

        // Set footnote value from saved data
        this.footnotes = superTableSavedData.footnotes || [];
        const footnoteValue = this.footnotes[0] || this.currentFootnoteValue || '';
        this.currentFootnoteValue = footnoteValue;
        this.form.controls['footnote'].setValue(footnoteValue);

        this.setLastGivenIds();


        this.handleFormValueChange(true);

        this.form
            .get('rows')
            .valueChanges.pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                startWith(this.form.value.rows),
                pairwise(),
                untilDestroyed(this),
            )
            .subscribe(([previousValue, currentValue]) => {
                const recalculate = this.shouldRecalculate(previousValue, currentValue);

                this.handleFormValueChange(recalculate);
            });

        this.form
            .get('tableTitle')
            .valueChanges.pipe(
                startWith(this.title?.[this.language]),
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                pairwise(),
                untilDestroyed(this),
            )
            .subscribe(([prevValue, currentValue]) => {
                this.handleFormValueChange(false);
            });

        this.form
            .get('footnote')
            .valueChanges.pipe(
                startWith(this.currentFootnoteValue || ''),
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                pairwise(),
                untilDestroyed(this),
            )
            .subscribe(([prevValue, currentValue]) => {
                this.handleFormValueChange(false);
            });

        (this.form.get('rows') as FormArray).controls.forEach((group: FormGroup, index: number) => {
            this.subscribeToRowTitleChanges(group, index);
        });
    }

    private subscribeToRowTitleChanges(rowGroup: FormGroup, index: number): void {
        // UPDATE CALCULATION TITLE INSIDE CALCULATION OBJECT IF IT IS EDITED FROM THE SUPER TABLE FORM
        rowGroup
            .get('title')
            ?.valueChanges.pipe(untilDestroyed(this))
            .subscribe((value) => {
                if ((rowGroup.value as ISuperTableComponentRowFormValue).type !== SuperTableRowType.CALCULATION) {
                    return;
                }

                const matchingCalculation = this.calculations.find(
                    (calc) => calc.calcId === (rowGroup.value as ISuperTableComponentRowFormValue).rowId,
                );

                matchingCalculation.title = value;
            });
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    removeRow(rowToRemoveIndex: number): void {
        this.closeRowContextFlyout();
        this.rowToRemoveIndex = rowToRemoveIndex;
        this.showRowDeletionDialog = true;
    }

    public onCancelRowDeletion(): void {
        this.showRowDeletionDialog = false;
        this.rowToRemoveIndex = null;
    }

    public onDeleteRow(): void {
        this.handleRowRemoval(this.rowToRemoveIndex);
        this.showRowDeletionDialog = false;
        this.rowToRemoveIndex = null;
    }

    private handleRowRemoval(rowToRemoveIndex: number): void {
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        const rowToRemoveValue: ISuperTableComponentRowFormValue = cloneDeep<ISuperTableComponentRowFormValue>(
            rowsFormArray.at(rowToRemoveIndex).value,
        );
        this.removeRowFromCalculations(rowToRemoveValue.rowId);
        rowsFormArray.removeAt(rowToRemoveIndex);
        this.setLastGivenIds();
        this.handleFormValueChange();
    }

    private removeRowFromCalculations(rowIdToRemove: string): void {
        const calculationsToBeRemoved: string[] = [];
        for (const calculation of this.calculations) {
            // row removed is a calculation row
            if (calculation.calcId === rowIdToRemove) {
                calculationsToBeRemoved.push(calculation.calcId);
            }

            // row removed is a ratio reference row, so we set calc to #REF!
            if (
                calculation.function === ISuperTableCalculationFunction.RATIO &&
                calculation.relationItemId === rowIdToRemove
            ) {
                // Set calculation as invalid for both row and column calculations
                this.setCalculationInvalidRef(calculation.calcId);
            }



            if (calculation.rowsIds.includes(rowIdToRemove)) {

                if (calculation.calcAxis === ISuperTableCalculationAxis.ROW) {
                    this.setCalculationInvalidRef(calculation.calcId);

                }
            }


        }

        this.calculations = this.calculations.filter((calc) => !calculationsToBeRemoved.includes(calc.calcId));
    }

    getCurrencyForColumn(col: IXAxisConfigColumn): string {
        // undefined  = never explicitly set → fall back to report default
        // ''         = explicitly cleared by user → show blank
        // 'CHF' etc. = explicitly set value → use it
        return col.currency !== undefined ? col.currency : this.defaultCurrencyForColumn(col);
    }

    /** FX (REV-21512): twin columns default to the display currency, everything else to the report currency. */
    private defaultCurrencyForColumn(col: IXAxisConfigColumn): string {
        return this.isFxColumn(col) ? this.fxNotesContext?.displayCurrency ?? this.currency : this.currency;
    }

    private isFxColumn(col: IXAxisConfigColumn): boolean {
        return SuperTableHelper.isFxColumnType(col.type);
    }

    saveCurrencyForColumn(colIndex: number, value: string): void {
        const trimmed = value.trim();
        // Store trimmed value directly: '' means "explicitly cleared", any text means "explicitly set".
        // undefined is reserved for "never touched" (initial state before user interacts with the field).
        this.xAxisConfigCols[colIndex] = {
            ...this.xAxisConfigCols[colIndex],
            currency: trimmed,
        };
        this.handleFormValueChange(false);
    }

    onChangeColumnTitle(event: Event, columnToEdit: IXAxisConfigColumn): void {
        const newTitle = (event.target as HTMLInputElement).value;
        const matchingColumn = this.xAxisConfigCols.find((col) => col.columnId === columnToEdit.columnId);

        if (matchingColumn.title) {
            matchingColumn.title = {
                ...matchingColumn.title,
                [this.language]: newTitle,
            };
        } else {
            matchingColumn.title = {
                [this.language]: newTitle,
            };
        }

        if (matchingColumn.type === SuperTableColumnType.CALCULATION) {
            const matchingCalculation = this.calculations.find((calc) => calc.calcId === columnToEdit.columnId);
            matchingCalculation.title = newTitle;
        }

        this.handleFormValueChange(false);
    }

    removeColumn(columnToRemove: IXAxisConfigColumn): void {
        this.columnToRemove = columnToRemove;
        this.showColumnDeletionDialog = true;
    }

    removeColumnByIndex(index: number): void {
        const columnToRemove = this.xAxisConfigCols[index];
        if (columnToRemove) {
            this.removeColumn(columnToRemove);
        }
        this.closeColumnContextFlyout();
    }

    private handleColumnRemoval(columnToRemove: IXAxisConfigColumn): void {
        const columnToRemoveId = columnToRemove.columnId;
        this.xAxisConfigCols = this.xAxisConfigCols.filter((col) => col.columnId !== columnToRemoveId);
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        if (columnToRemove.type === SuperTableColumnType.PREVIOUS_YEAR) {
            this.compareYears = false;
            rowsFormArray.controls.forEach((row) => {
                (row as FormGroup).removeControl('previousValue');
            });
        } else if (columnToRemove.type === SuperTableColumnType.CURRENT_YEAR_FX) {
            // FX (REV-21512): removing a twin flips its hide-flag (like PREVIOUS_YEAR flips
            // compareYears) and drops the user-input controls, exactly like previousValue.
            this.fxHideCurrentYear = true;
            rowsFormArray.controls.forEach((row) => {
                (row as FormGroup).removeControl(columnToRemoveId);
            });
        } else if (columnToRemove.type === SuperTableColumnType.PREVIOUS_YEAR_FX) {
            this.fxHidePreviousYear = true;
            rowsFormArray.controls.forEach((row) => {
                (row as FormGroup).removeControl(columnToRemoveId);
            });
        } else {
            rowsFormArray.controls.forEach((row) => {
                (row as FormGroup).removeControl(columnToRemoveId);
            });
        }

        this.removeColumnFromCalculations(columnToRemoveId);
        this.removeColumnFromSegmentChildren(columnToRemoveId);
        this.setLastGivenIds();
        this.handleFormValueChange();
    }

    private removeColumnFromCalculations(columnToRemoveId: string): void {
        const calculationsToBeRemoved: string[] = [];

        for (const calculation of this.calculations) {
            // column removed is a calculation column
            if (calculation.calcId === columnToRemoveId) {
                calculationsToBeRemoved.push(calculation.calcId);
            }

            // column removed is a ratio reference column, so we set all cells to #REF!
            if (
                calculation.function === ISuperTableCalculationFunction.RATIO &&
                calculation.calcAxis === ISuperTableCalculationAxis.COLUMN &&
                calculation.columnsIds.includes(columnToRemoveId)
            ) {
                this.setCalculationInvalidRef(calculation.calcId);
            }

            if (calculation.columnsIds?.includes(columnToRemoveId)) {
                calculation.columnsIds = calculation.columnsIds.filter((rowId) => rowId !== columnToRemoveId);
                this.setCalculationInvalidRef(calculation.calcId);
            }


        }

        this.calculations = this.calculations.filter((calc) => !calculationsToBeRemoved.includes(calc.calcId));
    }

    private removeColumnFromSegmentChildren(columnIdToRemove: string): void {
        let segIndex = 0;
        const segmentIndexesToBeRemoved: number[] = [];
        for (const segment of this.segments) {
            if (!segment.childColIds.includes(columnIdToRemove)) {
                continue;
            }

            segment.childColIds = segment.childColIds.filter((col) => col !== columnIdToRemove);

            if (segment.childColIds.length === EMPTY_ARRAY_LENGTH) {
                segmentIndexesToBeRemoved.push(segIndex);
            }
            segIndex = segIndex + 1;
        }

        // Sort the indexes from largest to smallest
        segmentIndexesToBeRemoved.sort((a, b) => b - a);

        // Now remove items one by one in descending order
        for (let i = 0; i < segmentIndexesToBeRemoved.length; i++) {
            this.segments.splice(segmentIndexesToBeRemoved[i], 1);
        }

        this.prepareStartingColumnIdToSegmentMap();
    }

    private removeColumnByColumnId(columnIdToRemove: string): void {
        this.xAxisConfigCols = this.xAxisConfigCols.filter((col) => col.columnId !== columnIdToRemove);
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        rowsFormArray.controls.forEach((row) => {
            (row as FormGroup).removeControl(columnIdToRemove);
        });
        this.removeColumnFromSegmentChildren(columnIdToRemove);
    }

    addNewRow(selectedOption: SuperTableAddRow): void {
        this.openAddNewRowDialog = false;
        switch (selectedOption) {
            case SuperTableAddRow.BALANCE:
                this.addRowFromBalanceSheet();
                break;
            case SuperTableAddRow.INCOME:
                this.addRowFromIncomeStatement();
                break;
            case SuperTableAddRow.FREE_TEXT:
                this.addFreeTextRow();
                break;
            case SuperTableAddRow.TEXT_ROW:
                this.addTextRow();
                break;
            case SuperTableAddRow.TITLE:
                this.addTitleRow();
                break;
            case SuperTableAddRow.SPACE:
                this.addSpaceRow();
                break;
            case SuperTableAddRow.CALCULATION:
                this.addCalculationRow();
                break;
            case SuperTableAddRow.SEGMENT:
                this.createSegment();
                break;
            case SuperTableAddRow.RATE:
                // FX (REV-21512): the source (closing/average/position) is picked in a
                // follow-up modal — same two-step flow as CALCULATION.
                this.openRateSourceSelection = true;
                break;
        }
    }

    /**
     * FX (REV-21512): source picked in the rate modal — just add the row, the table itself is
     * never touched (Ruling Janosch: columns are managed solely via the hide-column flyout).
     */
    onRateSourceSelection(source: 'closing' | 'average' | 'position'): void {
        this.openRateSourceSelection = false;
        if (source === 'position') {
            // Reuse the mapping picker: the picked nodes become the row's itemsIds; the
            // pending flag turns the resulting row into a RATE row.
            this.pendingRateRowSource = 'position';
            this.openAddNewRowFromBalance = true;

            return;
        }
        // Default title like the position row gets from the picker — editable afterwards.
        const defaultTitle = this.i18NextService.t(
            source === 'closing'
                ? 'aumico_notes_super_table_rate_source_closing'
                : 'aumico_notes_super_table_rate_source_average',
        ) as string;
        this.pushRateRow(source, [], defaultTitle);
    }

    onCancelRateSourceSelection(): void {
        this.openRateSourceSelection = false;
    }

    private pushRateRow(source: 'closing' | 'average' | 'position', itemsIds: string[], title?: string | null): void {
        const rateRow: ISuperTableComponentRowFormValue = {
            rowId: undefined,
            title: title ?? null,
            type: SuperTableRowType.RATE,
            itemsIds,
            currentValue: undefined,
            previousValue: undefined,
            format: undefined,
            reconcileItems: null,
            financialType: undefined,
            rateSource: source,
        };
        (this.form.get('rows') as UntypedFormArray).push(this.getRowAsFormGroup(rateRow));
        this.handleFormValueChange(true);
    }

    addTextRow(): void {
        if (this.transpose && this.form.get('rows').value.length >= MAX_ROW_NUMBER) {
            return;
        }

        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        const textRow: ISuperTableComponentRowFormValue = {
            rowId: undefined,
            title: null,
            type: SuperTableRowType.TEXT_ROW,
            itemsIds: [],
            format: {
                styles: {} as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>,
                numberOfDecimals: {
                    value: TWO_DECIMAL_PLACES,
                },
            },
        };

        const newRowFormGroup = this.getRowAsFormGroup(textRow);
        rowsFormArray.push(newRowFormGroup);
    }

    addFreeTextRow(): void {
        if (this.transpose && this.form.get('rows').value.length >= MAX_ROW_NUMBER) {
            return;
        }

        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        const emptyFreeTextRow: ISuperTableComponentRowFormValue = {
            rowId: undefined,
            title: null,
            type: SuperTableRowType.FREE_TEXT,
            itemsIds: [],
            format: {
                styles: {} as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>,
                numberOfDecimals: {
                    value: TWO_DECIMAL_PLACES,
                },
            },
        };

        const newRowFormGroup = this.getRowAsFormGroup(emptyFreeTextRow);

        rowsFormArray.push(newRowFormGroup);
    }

    addRowFromBalanceSheet(): void {
        if (this.transpose && this.form.get('rows').value.length >= MAX_ROW_NUMBER) {
            return;
        }

        this.openAddNewRowFromBalance = true;
    }

    addRowFromIncomeStatement(): void {
        if (this.transpose && this.form.get('rows').value.length >= MAX_ROW_NUMBER) {
            return;
        }

        this.openAddNewRowFromIncome = true;
    }

    addTitleRow(): void {
        if (this.transpose && this.form.get('rows').value.length >= MAX_ROW_NUMBER) {
            return;
        }

        const rowsFormArray = this.form.get('rows') as UntypedFormArray;

        const titleRow: ISuperTableComponentRowFormValue = {
            rowId: undefined,
            title: null,
            type: SuperTableRowType.TITLE,
            itemsIds: [],
            format: {
                styles: {} as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>,
                numberOfDecimals: {
                    value: TWO_DECIMAL_PLACES,
                },
            },
        };

        const newRowFormGroup = this.getRowAsFormGroup(titleRow);

        rowsFormArray.push(newRowFormGroup);
    }

    addSpaceRow(): void {
        if (this.transpose && this.form.get('rows').value.length >= MAX_ROW_NUMBER) {
            return;
        }

        const rowsFormArray = this.form.get('rows') as UntypedFormArray;

        const spaceRow: ISuperTableComponentRowFormValue = {
            rowId: undefined,
            title: null,
            type: SuperTableRowType.SPACE,
            itemsIds: [],
        };

        const newRowFormGroup = this.getRowAsFormGroup(spaceRow);

        rowsFormArray.push(newRowFormGroup);
    }

    addCalculationRow(): void {
        if (this.transpose && this.form.get('rows').value.length >= MAX_ROW_NUMBER) {
            return;
        }

        this.currentSelectedAxis = ISuperTableCalculationAxis.ROW;

        this.openCalculationFunctionSelection = true;
    }

    onCalculationFunctionSelection(calculationFunction: ISuperTableCalculationFunction): void {
        this.currentSelectedCalculationFunction = calculationFunction;
        this.rowSelections = this.getAddCalculationRowsSelections();
        this.columnSelections = this.getAddCalculationColumnsSelections();

        switch (this.currentSelectedCalculationFunction) {
            case ISuperTableCalculationFunction.SUMMATION:
                if (
                    this.currentSelectedAxis === ISuperTableCalculationAxis.COLUMN ||
                    this.activeContextMenuColumn != null
                ) {
                    this.openSummationColumnDialog = true;
                } else if (
                    this.currentSelectedAxis === ISuperTableCalculationAxis.ROW ||
                    this.activeContextMenuRow != null
                ) {
                    this.openSummationRowDialog = true;
                }
                break;
            case ISuperTableCalculationFunction.RATIO:
                if (
                    this.currentSelectedAxis === ISuperTableCalculationAxis.COLUMN ||
                    this.activeContextMenuColumn != null
                ) {
                    this.openRatioColumnDialog = true;
                } else if (
                    this.currentSelectedAxis === ISuperTableCalculationAxis.ROW ||
                    this.activeContextMenuRow != null
                ) {
                    this.openRatioRowDialog = true;
                }
                break;

            case ISuperTableCalculationFunction.SUBTRACTION:
                if (
                    this.currentSelectedAxis === ISuperTableCalculationAxis.COLUMN ||
                    this.activeContextMenuColumn != null
                ) {
                    this.openSubtractionColumnDialog = true;
                } else if (
                    this.currentSelectedAxis === ISuperTableCalculationAxis.ROW ||
                    this.activeContextMenuRow != null
                ) {
                    this.openSubtractionRowDialog = true;
                }
                break;

            case ISuperTableCalculationFunction.MULTIPLICATION:
                if (
                    this.currentSelectedAxis === ISuperTableCalculationAxis.COLUMN ||
                    this.activeContextMenuColumn != null
                ) {
                    this.openMultiplicationColumnDialog = true;
                } else if (
                    this.currentSelectedAxis === ISuperTableCalculationAxis.ROW ||
                    this.activeContextMenuRow != null
                ) {
                    this.openMultiplicationRowDialog = true;
                }
                break;
        }

        this.openCalculationFunctionSelection = false;
    }

    onSaveCalculationRow(): void {
        //EDIT MODE
        if (this.currentSelectedCalculation != null) {
            this.modifyCalculation();
            return;
        }
        // ADD NEW CALCULATION COLUMN
        else if (this.currentSelectedCalculation == null) {
            this.createNewCalculation(ISuperTableCalculationAxis.ROW);
        }
    }

    onSaveCalculationColumn(): void {
        //EDIT MODE
        if (this.currentSelectedCalculation != null) {
            this.modifyCalculation();
            return;
        }
        // ADD NEW CALCULATION COLUMN
        else if (this.currentSelectedCalculation == null) {
            this.createNewCalculation(ISuperTableCalculationAxis.COLUMN);
        }
    }

    private modifyCalculation(): void {
        const matchingCalculationIndex = this.calculations.findIndex(
            (calc) => calc.calcId === this.currentSelectedCalculation.calcId,
        );
        const editedCalculation: ISuperTableCalculation = {
            calcId: this.currentSelectedCalculation.calcId,
            title: this.currentCalculationOutput.title,
            function: this.currentCalculationOutput.function,
            calcAxis: this.currentSelectedCalculation.calcAxis,
            rowsIds: this.currentCalculationOutput.rowSelections.map((selection) => selection.name),
            columnsIds: this.currentCalculationOutput.columnSelections?.map((selection) => selection.id),
            relationItemId: this.currentCalculationOutput.relationItem?.name,
        };

        this.clearCalculationInvalidRef(editedCalculation.calcId);

        this.calculations[matchingCalculationIndex] = cloneDeep<ISuperTableCalculationWithInvalidRef>(editedCalculation);
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        const indexInRows = rowsFormArray.value.findIndex(
            (row) => row.rowId === this.currentSelectedCalculation.calcId,
        );
        // Case Calculation is Calculation Row
        if (indexInRows != -1) {
            const rowToEditValue: ISuperTableComponentRowFormValue = cloneDeep<ISuperTableComponentRowFormValue>(
                rowsFormArray.at(indexInRows).value,
            );

            rowToEditValue.title = this.currentCalculationOutput.title;

            rowsFormArray.at(indexInRows).patchValue(rowToEditValue);
            // Case Calculation is Calculation Column
        } else {
            const matchingColumn = this.xAxisConfigCols.find(
                (col) => col.columnId === this.currentSelectedCalculation.calcId,
            );

            if (matchingColumn == null) {
                throw new Error('edited calculation has no corresponding row or column');
            }
            matchingColumn.title = {
                ...matchingColumn.title,
                [this.language]: this.currentCalculationOutput.title,
            };

            matchingColumn.belongingYear = this.currentCalculationOutput.belongingYear;
            matchingColumn.columnWidth = this.currentCalculationOutput.columnWidth || SuperTableColumnWidth.STANDARD;
        }


        this.handleFormValueChange(true);

        this.onCancelCalculation(false);
    }

    private createNewCalculation(calculationAxis: ISuperTableCalculationAxis): void {
        let calculation: ISuperTableCalculation;

        if (this.currentCalculationOutput.function === ISuperTableCalculationFunction.RATIO) {
            calculation = {
                calcId: 'row-' + (this.lastGivenRowIndex + 1),
                title: this.currentCalculationOutput.title,
                function: this.currentCalculationOutput.function,
                calcAxis: calculationAxis,
                rowsIds: this.currentCalculationOutput.rowSelections.map((selection) => selection.name),
                relationItemId: this.currentCalculationOutput.relationItem.name,
                columnsIds: [this.currentCalculationOutput.columnSelections].flat().map((selection) => selection.id),
            };
        } else {
            calculation = {
                calcId: 'row-' + (this.lastGivenRowIndex + 1),
                title: this.currentCalculationOutput.title,
                function: this.currentCalculationOutput.function,
                calcAxis: calculationAxis,
                rowsIds: this.currentCalculationOutput.rowSelections.map((selection) => selection.name),
                columnsIds: this.currentCalculationOutput.columnSelections?.map((selection) => selection.id),
            };
        }

        if (calculationAxis === ISuperTableCalculationAxis.ROW) {
            this.calculations.push(calculation);

            this.addCalculationRowToForm(calculation);

            this.setLastGivenIds();
        } else if (calculationAxis === ISuperTableCalculationAxis.COLUMN) {
            const calculationColumnId = 'col' + (this.lastGivenColumnIndex + 1);

            const rowsFormArray = this.form.get('rows') as UntypedFormArray;

            rowsFormArray.controls.forEach((control: FormGroup) => {
                control.addControl(calculationColumnId, new UntypedFormControl(undefined));
            });

            const calculationColumn: IXAxisConfigColumn = {
                columnId: calculationColumnId,
                title: { [this.language]: calculation.title },
                type: SuperTableColumnType.CALCULATION,
                belongingYear: this.currentCalculationOutput.belongingYear,
                format: {
                    styles: { [AumicoStyleOptions.RIGHT_ALIGNED]: { enabled: true, appliedAt: new Date() } } as Record<
                        AumicoStyleOptions,
                        { enabled: boolean; appliedAt: Date }
                    >,
                    numberOfDecimals: {
                        value: TWO_DECIMAL_PLACES,
                    },
                },
                columnWidth: this.currentCalculationOutput.columnWidth || SuperTableColumnWidth.STANDARD,
            };

            if (this.currentCalculationOutput.function === ISuperTableCalculationFunction.RATIO) {
                // Automatically add % Format Option
                calculationColumn.format.styles[AumicoStyleOptions.PERCENTAGE] = {
                    enabled: true,
                    appliedAt: new Date(),
                };

                const referenceColumnId = this.currentCalculationOutput.columnSelections[FIRST_ELEMENT_INDEX].id;
                const referenceColumnIndex = this.xAxisConfigCols.findIndex(
                    (col) => col.columnId === referenceColumnId,
                );
                this.xAxisConfigCols.splice(referenceColumnIndex + 1, 0, calculationColumn);
            } else {
                this.xAxisConfigCols.push(calculationColumn);
            }

            this.setLastGivenIds();

            this.applyColumnCalculations(calculation, calculationColumnId);

            calculation.calcId = calculationColumnId;

            this.calculations.push(calculation);
        }

        this.handleFormValueChange();

        this.onCancelCalculation(false);
    }

    onCancelCalculation(showCalculationSelectionPopup: boolean): void {
        this.openSummationRowDialog = false;
        this.openSubtractionRowDialog = false;
        this.openMultiplicationRowDialog = false;
        this.openRatioRowDialog = false;
        this.openColumnCalculationFunctionSelection = false;
        this.openSummationColumnDialog = false;
        this.openSubtractionColumnDialog = false;
        this.openMultiplicationColumnDialog = false;
        this.openRatioColumnDialog = false;
        this.currentSelectedCalculationFunction = undefined;
        this.currentSelectedCalculation = undefined;
        this.currentCalculationOutput = undefined;
        this.openCalculationFunctionSelection = showCalculationSelectionPopup;
        this.currentSelectedCalculationFunction = undefined;

        if (!showCalculationSelectionPopup) {
            this.currentSelectedAxis = null;
        }

        this.closeColumnContextFlyout();
        this.closeRowContextFlyout();
    }

    createSegment(): void {
        const columnIdsBelongingToSegments = this.segments.map((seg) => seg.childColIds).flat();
        const columnsThatDoNotBelongToASegment = this.xAxisConfigCols.filter(
            (col) => !columnIdsBelongingToSegments.includes(col.columnId) && !this.isFxColumn(col),
        );
        this.columnOptions = columnsThatDoNotBelongToASegment.map((col) => {
            return {
                id: col.columnId,
                name:
                    col.type === SuperTableColumnType.CURRENT_YEAR
                        ? (this.i18NextService.t('aumico_notes_super_table_options_col_type_current_year') as string)
                        : col.type === SuperTableColumnType.PREVIOUS_YEAR
                            ? (this.i18NextService.t('aumico_notes_super_table_options_col_type_previous_year') as string)
                            : col.title[this.language],
            };
        });

        this.showAddNewSegementDialog = true;
    }

    onSegmentFormValueCreationChange(segment: ISuperTableCreateSegmentDialogResult): void {
        this.segementFormValue = segment;
    }

    onSegementCreate(): void {
        if (this.segementFormValue == null) {
            return;
        }

        const selectedIds = this.segementFormValue.selectedColumns.map((col) => col.id);
        if (
            !this.areIDsOrdered(
                this.xAxisConfigCols.map((col) => col.columnId),
                selectedIds,
            )
        ) {
            this.rearrangeColumnsByColumnIds(selectedIds);
        }

        // Handle editing an existing segment
        if (this.segmentToEdit) {
            const segmentIndex = this.segments.findIndex((seg) => seg.segmentId === this.segmentToEdit.segmentId);
            if (segmentIndex > -1) {
                const updatedSegment = {
                    ...this.segments[segmentIndex],
                    title: {
                        ...this.segments[segmentIndex].title,
                        [this.language]: this.segementFormValue.segmentTitle,
                    },
                    childColIds: selectedIds,
                };
                this.segments[segmentIndex] = updatedSegment;
                // Make it an immutable update to trigger change detection if needed
                this.segments = [...this.segments];
            }
        } else {
            // Handle creating a new segment (original logic)
            const newSegment: ISuperTableSegment = {
                segmentId: 'segment' + this.segments.length,
                title: {
                    [this.language]: this.segementFormValue.segmentTitle,
                },
                childColIds: selectedIds,
            };
            this.segments = [...this.segments, newSegment];
        }

        this.handleFormValueChange(false);

        this.prepareStartingColumnIdToSegmentMap();

        this.showAddNewSegementDialog = false;
        this.segementFormValue = undefined;
        this.segmentToEdit = null;
    }

    private rearrangeColumnsByColumnIds(orderedColumnIds: string[]): void {
        const priorityMap = new Map<string, number>();
        orderedColumnIds.forEach((id, index) => {
            priorityMap.set(id, index);
        });

        const columnsToReorder = this.xAxisConfigCols.filter((col) => priorityMap.has(col.columnId));

        columnsToReorder.sort((a, b) => {
            return (priorityMap.get(a.columnId) || 0) - (priorityMap.get(b.columnId) || 0);
        });

        const orderedColumns = [];
        let reorderIndex = 0;
        for (const col of this.xAxisConfigCols) {
            if (priorityMap.has(col.columnId)) {
                orderedColumns.push(columnsToReorder[reorderIndex]);
                reorderIndex++;
            } else {
                orderedColumns.push(col);
            }
        }

        this.xAxisConfigCols = orderedColumns;
    }

    onCancelSegmentCreation(): void {
        this.showAddNewSegementDialog = false;
        this.segementFormValue = null;
        this.segmentToEdit = null;
    }

    public editSegment(segmentToEdit: ISuperTableSegment): void {
        // Set the segment to be edited. The dialog component will use this as its Input.
        this.segmentToEdit = cloneDeep(segmentToEdit);

        // The dialog still needs the list of available columns to render its selection dropdown.
        const columnIdsBelongingToOtherSegments = this.segments
            .filter((seg) => seg.segmentId !== segmentToEdit.segmentId)
            .map((seg) => seg.childColIds)
            .flat();

        const availableColumns = this.xAxisConfigCols.filter(
            (col) => !columnIdsBelongingToOtherSegments.includes(col.columnId) && !this.isFxColumn(col),
        );

        this.columnOptions = availableColumns.map((col) => {
            return {
                id: col.columnId,
                name:
                    col.type === SuperTableColumnType.CURRENT_YEAR
                        ? (this.i18NextService.t('aumico_notes_super_table_options_col_type_current_year') as string)
                        : col.type === SuperTableColumnType.PREVIOUS_YEAR
                            ? (this.i18NextService.t('aumico_notes_super_table_options_col_type_previous_year') as string)
                            : col.title[this.language],
            };
        });

        // We just open the dialog. The dialog component is responsible for populating its own form.
        this.showAddNewSegementDialog = true;
    }

    onEditRowSelectionClick(rowToEditIndex: number): void {
        this.isRowContextFlyoutOpen = false;
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        const rowToEditValue: ISuperTableComponentRowFormValue = cloneDeep<ISuperTableComponentRowFormValue>(
            rowsFormArray.at(rowToEditIndex).value,
        );

        if (rowToEditValue.type === SuperTableRowType.CALCULATION) {
            this.editCalculationRow(rowToEditValue);
            return;
        }

        this.allMappingItemsSelectionTree = {
            data: null,
            children: this.balanceMappingItemsSelectionTree.children.concat(this.incomeMappingItemsSelectionTree),
            type: ItemType.GROUP,
            financialType: null,
            expanded: true,
        };

        if (rowToEditValue.itemsIds?.length === EMPTY_ARRAY_LENGTH || rowToEditValue.financialType == null) {
            // User needs to decide to map from income or from balance
            // Ask the user which mapping to use
            const question = this.i18NextService.t('aumico_notes_super_table_select_income_or_balance') as string;
            const balanceLabel = this.i18NextService.t('aumico_annual_report_import_form_balance_title') as string;
            const incomeLabel = this.i18NextService.t('aumico_annual_report_import_form_income_title') as string;

            const dialogRef = this.dialog.open(SelectMappingDialogComponent, {
                width: '400px',
                data: {
                    message: question,
                    options: [
                        { label: balanceLabel, value: 'balance' },
                        { label: incomeLabel, value: 'income' },
                    ],
                },
            });

            this.disableOutsideClick = true;
            dialogRef
                .afterClosed()
                .pipe(
                    filter((result) => result != null),
                    untilDestroyed(this),
                )
                .subscribe((result: 'balance' | 'income') => {
                    this.disableOutsideClick = false;
                    if (result === 'balance') {
                        rowToEditValue.financialType = TemplateType.BALANCE;
                    } else if (result === 'income') {
                        rowToEditValue.financialType = TemplateType.INCOME;
                    }
                    rowsFormArray.at(rowToEditIndex).patchValue(rowToEditValue);
                    this.openAddNewRowFromBalanceOrIncome = true;
                });
        } else {
            this.openAddNewRowFromBalanceOrIncome = true;
        }
    }

    editCalculationRow(rowToEditValue: ISuperTableComponentRowFormValue): void {
        const matchingCalculation = this.calculations.find((calc) => calc.calcId === rowToEditValue.rowId);

        if (matchingCalculation == null) {
            throw new Error('calculation row not found');
        }

        this.currentSelectedCalculationFunction = matchingCalculation.function;
        this.currentSelectedCalculation = cloneDeep<ISuperTableCalculation>(matchingCalculation);

        this.onCalculationFunctionSelection(this.currentSelectedCalculationFunction);
    }

    onMappingRowSettingsChange(mappingRowSettings: SuperTableMappingRowSettings): void {
        this.currentMappingRowSettings = cloneDeep<SuperTableMappingRowSettings>(mappingRowSettings);
    }

    handleMappingRowSettings() {
        this.openAddNewRowFromBalance = false;
        this.openAddNewRowFromIncome = false;
        this.openAddNewRowFromBalanceOrIncome = false;

        if (
            (this.currentMappingRowSettings == null ||
                this.currentMappingRowSettings.selectedItems.length === EMPTY_ARRAY_LENGTH) &&
            this.activeContextMenuRow == null
        ) {
            // add new undefined row
            const row: ISuperTableComponentRowFormValue = {
                rowId: undefined,
                title: this.currentMappingRowSettings?.rowTitle,
                type: SuperTableRowType.UNDEFINED,
                itemsIds: [],
                previousValue: undefined,
                currentValue: undefined,
                format: {
                    styles: {} as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>,
                    numberOfDecimals: {
                        value: TWO_DECIMAL_PLACES,
                    },
                },
            };

            const rowFormGroup = this.getRowAsFormGroup(row);
            const rowsFormArray = this.form.get('rows') as UntypedFormArray;
            rowsFormArray.push(rowFormGroup);
            this.handleFormValueChange(true);
            this.currentMappingRowSettings = null;
            return;
        }

        const rowsFormArray = this.form.get('rows') as UntypedFormArray;

        // REV-20899: include each selected node's aggregated reclass in the sum.
        const itemsCurrentValues = this.currentMappingRowSettings.selectedItems.map(
            (item) => this.finalCYValue(item.data),
        );
        const currentValuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(itemsCurrentValues, 0);

        const itemsPreviousValues = this.currentMappingRowSettings.selectedItems.map(
            (item) => this.finalPYValue(item.data),
        );
        const previousValuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(itemsPreviousValues, 0);

        // FX (REV-21512): the translation reserve exists only in display currency — its
        // functional cells stay EMPTY, never 0 (same rule as resetFormValuesFromMappingItems).
        // The length guard matters: `[].every()` is true, and without it an EMPTY selection would
        // blank the cells instead of writing the summation's 0 — on every report, FX or not.
        const onlyReserveNodesPicked =
            this.currentMappingRowSettings.selectedItems.length > EMPTY_ARRAY_LENGTH &&
            this.currentMappingRowSettings.selectedItems.every(
                (item) => item.data?.name === FX_RESERVE_NODE_NAME,
            );

        const rowType =
            this.currentMappingRowSettings.selectedItems.length === EMPTY_ARRAY_LENGTH
                ? SuperTableRowType.UNDEFINED
                : this.currentMappingRowSettings.selectedItems[FIRST_ELEMENT_INDEX].type;

        // editing an already existing row
        if (this.activeContextMenuRow != null) {
            const rowToEditValue: ISuperTableComponentRowFormValue = cloneDeep<ISuperTableComponentRowFormValue>(
                rowsFormArray.at(this.activeContextMenuRow).value,
            );

            rowToEditValue.itemsIds = this.currentMappingRowSettings.selectedItems.map((item) =>
                this.superTableMapper.getNodeIdentifier(item),
            );
            rowToEditValue.type = rowType as any;
            rowToEditValue.title = this.currentMappingRowSettings.rowTitle;
            rowToEditValue.previousValue = onlyReserveNodesPicked ? undefined : previousValuesSummation;
            rowToEditValue.currentValue = onlyReserveNodesPicked ? undefined : currentValuesSummation;
            rowToEditValue.financialType =
                this.currentMappingRowSettings.selectedItems[FIRST_ELEMENT_INDEX]?.financialType;

            rowsFormArray.at(this.activeContextMenuRow).patchValue(rowToEditValue);
            this.handleFormValueChange(true);
            this.currentMappingRowSettings = null;
            this.activeContextMenuRow = null;
            return;
        }

        // FX (REV-21512): the picker was opened for a position rate row — the picked nodes become
        // the row's reference, the values stay empty (the rate is derived, never stored).
        if (this.pendingRateRowSource === 'position') {
            this.pushRateRow(
                'position',
                this.currentMappingRowSettings.selectedItems.map((item) => this.superTableMapper.getNodeIdentifier(item)),
                this.currentMappingRowSettings.rowTitle,
            );
            this.pendingRateRowSource = null;
            this.currentMappingRowSettings = null;

            return;
        }

        // add new row
        const row: ISuperTableComponentRowFormValue = {
            rowId: undefined,
            title: this.currentMappingRowSettings.rowTitle,
            type: rowType as any,
            itemsIds: this.currentMappingRowSettings.selectedItems.map((item) =>
                this.superTableMapper.getNodeIdentifier(item)
            ),
            previousValue: onlyReserveNodesPicked ? undefined : previousValuesSummation,
            currentValue: onlyReserveNodesPicked ? undefined : currentValuesSummation,
            format: {
                styles: {} as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>,
                numberOfDecimals: {
                    value: TWO_DECIMAL_PLACES,
                },
            },
            financialType: this.currentMappingRowSettings.selectedItems[FIRST_ELEMENT_INDEX]?.financialType,
        };

        const rowFormGroup = this.getRowAsFormGroup(row);

        rowsFormArray.push(rowFormGroup);
        this.handleFormValueChange(true);
        this.currentMappingRowSettings = null;
    }

    private mapFormValueToSuperTableComponent(formValue: ISuperTableComponentFormValue): ISuperTableComponent {
        const rows: ISuperTableRow[] = formValue.rows.map((row) => {
            let rowToSave: ISuperTableRow;

            rowToSave = {
                rowId: row.rowId,
                title: row.title,
                type: row.type,
                itemsIds: row.itemsIds,
                previousValue: row.previousValue,
                currentValue: row.currentValue,
                format: row.format,
                reconcileItems: row.reconcileItems,
                financialtype: row.financialType,
                rateSource: row.rateSource,
            };

            for (const col of this.xAxisConfigCols) {
                if (col.type === SuperTableColumnType.CURRENT_YEAR || col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                    continue;
                }
                rowToSave[col.columnId] = row[col.columnId] as unknown as
                    | string[]
                    | ICellFormat
                    | ISuperTableReconcileItem[]
                    | number
                    | string
                    | null
                    | undefined;
            }

            return rowToSave;
        });

        const result = {
            rows,
            options: {
                title: { ...this.title, [this.language]: formValue.tableTitle },
                xAxisConfig: this.showCurrency
                    ? this.xAxisConfigCols.map((col) =>
                          col.currency === undefined ? { ...col, currency: this.defaultCurrencyForColumn(col) } : col,
                      )
                    : this.xAxisConfigCols,
                segments: this.segments,
                compareYears: this.compareYears,
                hideCurrentYear: this.hideCurrentYear,
                showFiscalYear: this.showFiscalYear,
                transpose: this.transpose,
                showPreviousYear: this.showPreviousYear,
                showCurrency: this.showCurrency,
                fxHideCurrentYear: this.fxHideCurrentYear,
                fxHidePreviousYear: this.fxHidePreviousYear,
            },
            calculations: this.calculations,
            referenceFlis: this.referenceFlis ?? this.superTableSavedData?.referenceFlis,
            addNumbering: this.addNumbering ?? this.superTableSavedData?.addNumbering,
            footnotes: this.footnotes,
        };
        return result;
    }

    private prepareStartingColumnIdToSegmentMap(): void {
        this.startingColumnIdToSegment.clear();
        const childColIds = [];

        for (const segment of this.segments) {
            this.startingColumnIdToSegment.set(segment.childColIds[FIRST_ELEMENT_INDEX], segment);
            childColIds.push(...segment.childColIds);
        }

        this.allColumnIdsUnderASegment = childColIds;
    }

    /**
     * Check if a calculation is marked as invalid (should show #REF!)
     */
    isCalculationInvalid(calcId: string): boolean {
        const calculation = this.calculations.find(calc => calc.calcId === calcId);
        return calculation ? calculation.invalidRef === true : false;
    }

    /**
     * Check if a cell should show #REF! based on its row and column
     */
    isCellInvalid(rowId: string, columnId?: string): boolean {
        // Check if the row itself is a calculation that's invalid
        if (this.isCalculationInvalid(rowId)) {
            return true;
        }

        // Check if the column is a calculation that's invalid
        if (columnId && this.isCalculationInvalid(columnId)) {
            return true;
        }

        return false;
    }

    private getAddCalculationRowsSelections(): ISuperTableItemSelection[] {
        const rowsSelections: ISuperTableItemSelection[] = [];
        const rows: ISuperTableComponentRowFormValue[] = this.form.get('rows').value;
        for (const row of rows) {
            if (row.type === SuperTableRowType.SPACE || row.type === SuperTableRowType.TITLE || row.type === SuperTableRowType.TEXT_ROW) {
                continue;
            }
            const extraColumnsValues: { [key: string]: string | number } = {};

            for (const col of this.xAxisConfigCols) {
                if (col.type === SuperTableColumnType.CURRENT_YEAR || col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                    continue;
                }

                extraColumnsValues[col.columnId] = row[col.columnId] as string | number;
            }

            rowsSelections.push({
                name: row.rowId,
                caption: row.title,
                currentValue: row.currentValue,
                previousValue: row.previousValue,
                type: row.type,
                ...extraColumnsValues,
            });
        }
        return rowsSelections;
    }

    private getAddCalculationColumnsSelections(): IXAxisConfigColumn[] {
        const columnSelections: IXAxisConfigColumn[] = [];

        for (const col of this.xAxisConfigCols) {
            if (col.type === SuperTableColumnType.TEXT) {
                continue;
            }
            // FX (REV-21512): twin values of mapping rows live outside the form (computed from
            // stamps), so a calculation COLUMN cannot operate on them — same exclusion as segments.
            if (this.isFxColumn(col)) {
                continue;
            }
            columnSelections.push(col);
        }
        return columnSelections;
    }

    private addCalculationRowToForm(calculation: ISuperTableCalculation): void {
        switch (calculation.function) {
            case ISuperTableCalculationFunction.SUMMATION: {
                const summationRow = this.superTableHelper.getSummationRow(
                    calculation,
                    this.form.value,
                    this.xAxisConfigCols,
                    this.compareYears,
                );
                this.pushCalculationRowToForm(summationRow);
                break;
            }

            case ISuperTableCalculationFunction.RATIO: {
                const ratioRow = this.superTableHelper.getRatioRow(
                    calculation,
                    this.form.value,
                    this.xAxisConfigCols,
                    this.compareYears,
                );
                ratioRow.format.styles[AumicoStyleOptions.PERCENTAGE] = {
                    enabled: true,
                    appliedAt: new Date(),
                };
                this.pushCalculationRowToForm(ratioRow);
                break;
            }
            case ISuperTableCalculationFunction.SUBTRACTION: {
                const subtractionRow = this.superTableHelper.getSubtractionRow(
                    calculation,
                    this.form.value,
                    this.xAxisConfigCols,
                    this.compareYears,
                );
                this.pushCalculationRowToForm(subtractionRow);
                break;
            }
            case ISuperTableCalculationFunction.MULTIPLICATION: {
                const multiplicationRow = this.superTableHelper.getMultiplicationRow(
                    calculation,
                    this.form.value,
                    this.xAxisConfigCols,
                    this.compareYears,
                );
                this.pushCalculationRowToForm(multiplicationRow);
                break;
            }
            default:
                throw new Error('function not supported');
        }
    }

    private pushCalculationRowToForm(calculationRow: ISuperTableComponentRowFormValue): void {
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;

        const newRowFormGroup = this.getRowAsFormGroup(calculationRow);

        rowsFormArray.push(newRowFormGroup);

        this.subscribeToRowTitleChanges(newRowFormGroup, this.lastGivenRowIndex + 1);
    }

    addNewColumn(selectedOption: SuperTableAddColumn): void {
        if (!this.transpose && this.xAxisConfigCols.length >= MAX_COLUMN_NUMBER) {
            return;
        }

        this.openAddNewColumnDialog = false;
        switch (selectedOption) {
            case SuperTableAddColumn.FREE_COLUMN:
                this.addFreeColumn();
                break;
            case SuperTableAddColumn.CALCULATION:
                this.addCalculationColumn();
                break;
        }
    }

    addCalculationColumn(): void {
        if (!this.transpose && this.xAxisConfigCols.length >= MAX_COLUMN_NUMBER) {
            return;
        }

        this.currentSelectedAxis = ISuperTableCalculationAxis.COLUMN;

        this.openCalculationFunctionSelection = true;
    }

    public addFreeColumn(): void {
        if (!this.transpose && this.xAxisConfigCols.length >= MAX_COLUMN_NUMBER) {
            return;
        }

        this.showAddFreeColumnDialog = true;
    }

    public onCreateFreeColumn(): void {
        if (!this.freeColumnFormIsValid) {
            return;
        }
        if (this.columnToEditIndex != null) {
            this.editColumn(this.columnToEditIndex, this.freeColumnFormValue);
            this.resetFreeColumnFormValue();
            return;
        }
        const columnId = 'col' + (this.lastGivenColumnIndex + 1);

        const rowsFormArray = this.form.get('rows') as UntypedFormArray;

        rowsFormArray.controls.forEach((control: FormGroup) => {
            control.addControl(
                columnId,
                new UntypedFormControl(
                    this.freeColumnFormValue.dataType === SuperTableColumnType.NUMBER ? 0 : undefined,
                ),
            );
        });

        this.xAxisConfigCols.push({
            columnId,
            title: { [this.language]: this.freeColumnFormValue.title },
            type: this.freeColumnFormValue.dataType,
            belongingYear: this.freeColumnFormValue.belongingYear,
            format: {
                styles:
                    this.freeColumnFormValue.dataType === SuperTableColumnType.NUMBER
                        ? ({ [AumicoStyleOptions.RIGHT_ALIGNED]: { enabled: true, appliedAt: new Date() } } as Record<
                            AumicoStyleOptions,
                            { enabled: boolean; appliedAt: Date }
                        >)
                        : ({} as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>),
                numberOfDecimals: { value: TWO_DECIMAL_PLACES },
            },
            columnWidth: this.freeColumnFormValue.columnWidth || SuperTableColumnWidth.STANDARD, // Default to STANDARD if not set
        });

        this.setLastGivenIds();
        this.prepareStartingColumnIdToSegmentMap();
        this.resetFreeColumnFormValue();
    }

    public onCancelFreeColumnCreation(): void {
        this.resetFreeColumnFormValue();
    }

    private resetFreeColumnFormValue(): void {
        this.showAddFreeColumnDialog = false;
        this.freeColumnFormValue = null;
        this.freeColumnFormIsValid = false;
        this.columnToEditIndex = null;
    }

    private applyColumnCalculations(calculation: ISuperTableCalculation, calculationColumnId: string): void {
        switch (calculation.function) {
            case ISuperTableCalculationFunction.SUMMATION:
                this.applySummationOfColumns(calculation, calculationColumnId);
                break;

            case ISuperTableCalculationFunction.RATIO: {
                this.applyRatioOfColumns(calculation, calculationColumnId);
                break;
            }
            case ISuperTableCalculationFunction.SUBTRACTION: {
                this.applySubtractionOfColumns(calculation, calculationColumnId);
                break;
            }
            case ISuperTableCalculationFunction.MULTIPLICATION: {
                this.applyMultiplicationOfColumns(calculation, calculationColumnId);
                break;
            }
            default:
                throw new Error('function not supported');
        }
    }

    private applySummationOfColumns(calculation: ISuperTableCalculation, calculationColumnId: string): void {
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;

        rowsFormArray.controls.forEach((control) => {
            if (control.get('type')?.value === SuperTableRowType.TITLE || control.get('type')?.value === SuperTableRowType.TEXT_ROW) {
                return; // don't patch calculation column for title/text rows
            }
            const rowValue = control.value;

            const values: number[] = calculation.columnsIds.map((colSelectionId) =>
                colSelectionId === 'CY'
                    ? rowValue.currentValue
                    : colSelectionId === 'PV'
                        ? rowValue.previousValue
                        : rowValue[colSelectionId],
            ) as number[];
            const valuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(values, 0);
            control.get(calculationColumnId).patchValue(valuesSummation);
        });
    }

    private applyRatioOfColumns(calculation: ISuperTableCalculation, calculationColumnId: string): void {
        const columnSelectionId = calculation.columnsIds[FIRST_ELEMENT_INDEX];

        const relationRow = (this.form.value as ISuperTableComponentFormValue).rows.find(
            (row) => row.rowId === calculation.relationItemId,
        );

        const referenceValue =
            columnSelectionId === 'CY'
                ? relationRow.currentValue
                : columnSelectionId === 'PV'
                    ? relationRow.previousValue
                    : (relationRow[columnSelectionId] as number);

        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        rowsFormArray.controls.forEach((control) => {
            if (control.get('type')?.value === SuperTableRowType.TITLE || control.get('type')?.value === SuperTableRowType.TEXT_ROW) {
                return; // don't patch calculation column for title/text rows
            }
            const rowValue = control.value;
            const value: number =
                columnSelectionId === 'CY'
                    ? rowValue.currentValue
                    : columnSelectionId === 'PV'
                        ? rowValue.previousValue
                        : (rowValue[columnSelectionId] as number);
            const ratio = NumberUtils.calculatePercentage(value, referenceValue);
            control.get(calculationColumnId).patchValue(isFinite(ratio) ? ratio : 0);
        });
    }

    private applySubtractionOfColumns(calculation: ISuperTableCalculation, calculationColumnId: string): void {
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        const mineundColumnId = calculation.columnsIds[FIRST_ELEMENT_INDEX];
        const subtrahendColumnId = calculation.columnsIds[SECOND_ELEMENT_INDEX];

        rowsFormArray.controls.forEach((control) => {
            if (control.get('type')?.value === SuperTableRowType.TITLE || control.get('type')?.value === SuperTableRowType.TEXT_ROW) {
                return; // don't patch calculation column for title/text rows
            }

            const rowValue = control.value;
            const minuendValue =
                mineundColumnId === 'CY'
                    ? rowValue.currentValue
                    : mineundColumnId === 'PV'
                        ? rowValue.previousValue
                        : rowValue[mineundColumnId];

            const subtrahendValue =
                subtrahendColumnId === 'CY'
                    ? rowValue.currentValue
                    : subtrahendColumnId === 'PV'
                        ? rowValue.previousValue
                        : rowValue[subtrahendColumnId];

            const valuesDifference = NumberUtils.subtractNumbers(
                !NumberUtils.isFinite(minuendValue) ? 0 : minuendValue,
                !NumberUtils.isFinite(subtrahendValue) ? 0 : subtrahendValue,
            );
            control.get(calculationColumnId).patchValue(valuesDifference);
        });
    }

    private applyMultiplicationOfColumns(calculation: ISuperTableCalculation, calculationColumnId: string): void {
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;

        rowsFormArray.controls.forEach((control) => {
            if (control.get('type')?.value === SuperTableRowType.TITLE || control.get('type')?.value === SuperTableRowType.TEXT_ROW) {
                return; // don't patch calculation column for title/text rows
            }
            const rowValue = control.value;

            const values: number[] = calculation.columnsIds.map((colSelectionId) =>
                colSelectionId === 'CY'
                    ? rowValue.currentValue
                    : colSelectionId === 'PV'
                        ? rowValue.previousValue
                        : rowValue[colSelectionId],
            ) as number[];
            let valuesMultiplication: number;

            if (values.length < 2 || values.includes(null)) {
                valuesMultiplication = 0;
            } else {
                valuesMultiplication = NumberUtils.multiplyNumbers(values);
            }
            control.get(calculationColumnId).patchValue(valuesMultiplication);
        });
    }

    private recalculateCalculationRowsAndColumns(): void {
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;

        for (const rowFormControl of rowsFormArray.controls) {
            const rowValue: ISuperTableComponentRowFormValue = rowFormControl.value;

            if (rowValue.type !== SuperTableRowType.CALCULATION) {
                continue;
            }

            const correspondingCalculation = this.calculations.find((calc) => calc.calcId === rowValue.rowId);

            if (correspondingCalculation == null) {
                continue;
            }

            switch (correspondingCalculation.function) {
                case ISuperTableCalculationFunction.SUMMATION: {
                    const summationRow = this.superTableHelper.getSummationRow(
                        correspondingCalculation,
                        this.form.value,
                        this.xAxisConfigCols,
                        this.compareYears,
                    );
                    summationRow.rowId = rowValue.rowId;
                    rowFormControl.patchValue(summationRow);
                    break;
                }

                case ISuperTableCalculationFunction.SUBTRACTION: {
                    const subtractionRow = this.superTableHelper.getSubtractionRow(
                        correspondingCalculation,
                        this.form.value,
                        this.xAxisConfigCols,
                        this.compareYears,
                    );
                    subtractionRow.rowId = rowValue.rowId;
                    rowFormControl.patchValue(subtractionRow);
                    break;
                }
                case ISuperTableCalculationFunction.RATIO: {
                    const ratioRow = this.superTableHelper.getRatioRow(
                        correspondingCalculation,
                        this.form.value,
                        this.xAxisConfigCols,
                        this.compareYears,
                    );
                    ratioRow.rowId = rowValue.rowId;
                    rowFormControl.patchValue(ratioRow);
                    break;
                }
                case ISuperTableCalculationFunction.MULTIPLICATION: {
                    const multiplicationRow = this.superTableHelper.getMultiplicationRow(
                        correspondingCalculation,
                        this.form.value,
                        this.xAxisConfigCols,
                        this.compareYears,
                    );
                    multiplicationRow.rowId = rowValue.rowId;
                    rowFormControl.patchValue(multiplicationRow);
                    break;
                }
                default:
                    throw new Error('calculation function not supported');
            }
        }

        for (const col of this.xAxisConfigCols) {
            if (col.type !== SuperTableColumnType.CALCULATION) {
                continue;
            }

            const correspondingCalculation = this.calculations.find((calc) => calc.calcId === col.columnId);
            if (correspondingCalculation == null) {
                continue;
            }
            this.applyColumnCalculations(correspondingCalculation, correspondingCalculation.calcId);
        }
    }

    public onFreeColumnFormValueCreationChange(freeColumnFormValue: ISuperTableAddFreeColumnDialogResult) {
        this.freeColumnFormValue = cloneDeep<ISuperTableAddFreeColumnDialogResult>(freeColumnFormValue);
    }

    public resetFormValuesFromMappingItems(): void {
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        for (const rowFormControl of rowsFormArray.controls) {
            const rowValue: ISuperTableComponentRowFormValue = rowFormControl.value;

            if (rowValue.itemsIds.length === EMPTY_ARRAY_LENGTH) {
                // Row was not selected from Balance or Income Mapping Items
                continue;
            }

            // FX (REV-21512): rate rows never carry stored values — their itemsIds only reference
            // the position whose effective rate is derived at render time.
            if (rowValue.type === SuperTableRowType.RATE) {
                continue;
            }

            let mappingTree: MappingItemsSelectionNode;
            if (rowValue.financialType) {
                mappingTree =
                    rowValue.financialType === TemplateType.BALANCE
                        ? this.balanceMappingItemsSelectionTree
                        : this.incomeMappingItemsSelectionTree;
            } else {
                mappingTree = {
                    data: null,
                    children: this.balanceMappingItemsSelectionTree.children.concat(
                        this.incomeMappingItemsSelectionTree,
                    ),
                    type: ItemType.GROUP,
                    financialType: null,
                    expanded: true,
                };
            }

            const matchingNodes = this.superTableMapper.findMatchingNodesForRowItems(rowValue, mappingTree);
            if (matchingNodes.length === EMPTY_ARRAY_LENGTH) {
                continue;
            }

            // REV-20899: aggregate reclass-aware values for refreshed rows.
            const itemsCurrentValues = matchingNodes.map((item) => this.finalCYValue(item.data));
            const currentValuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(itemsCurrentValues, 0);

            const itemsPreviousValues = matchingNodes.map((item) => this.finalPYValue(item.data));
            const previousValuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(itemsPreviousValues, 0);

            const rowType = matchingNodes[FIRST_ELEMENT_INDEX].type;

            // FX (REV-21512): the translation reserve exists only in display currency — its
            // functional cells stay EMPTY, never 0 (the reserve arises from translating).
            const onlyReserveNodes = matchingNodes.every((item) => item.data?.name === FX_RESERVE_NODE_NAME);

            const row: ISuperTableComponentRowFormValue = {
                rowId: rowValue.rowId,
                title: rowValue.title,
                type: rowType as any,
                itemsIds: matchingNodes.map((item) => this.superTableMapper.getNodeIdentifier(item)),
                previousValue: onlyReserveNodes ? undefined : previousValuesSummation,
                currentValue: onlyReserveNodes ? undefined : currentValuesSummation,
            };

            for (const col of this.xAxisConfigCols) {
                if (col.type === SuperTableColumnType.CURRENT_YEAR || col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                    continue;
                }
                row[col.columnId] = rowValue[col.columnId];
            }

            rowFormControl.patchValue(row);
        }

        this.handleFormValueChange(true);
    }

    /**
     * FX (REV-21512): derives every row's display-currency twin (and rate rows' rates) from the
     * stamped trees — on every form/tree change, never persisted (G2: the trees themselves refresh
     * only through the banner reset / page load).
     */
    private recomputeFxTwinValues(): void {
        this.fxTwinValues = new Map();
        if (this.fxNotesContext == null || this.form == null) {
            return;
        }
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        for (const rowFormControl of rowsFormArray.controls) {
            const rowValue: ISuperTableComponentRowFormValue = rowFormControl.value;
            if (rowValue.rowId == null) {
                continue;
            }
            if (rowValue.type === SuperTableRowType.RATE) {
                this.fxTwinValues.set(rowValue.rowId, this.fxRateValuesOf(rowValue));
                continue;
            }
            if (rowValue.itemsIds == null || rowValue.itemsIds.length === EMPTY_ARRAY_LENGTH) {
                // Typed rows have no reference — their twin cells stay empty by design (Thema 6).
                continue;
            }
            const stamps = this.fxStampsOf(rowValue);
            if (stamps.length === EMPTY_ARRAY_LENGTH) {
                continue;
            }
            const cyValues = stamps.map((stamp) => stamp.displayCY);
            const pyValues = stamps.map((stamp) => stamp.displayPY);
            this.fxTwinValues.set(rowValue.rowId, {
                cy: cyValues.some((value) => value == null)
                    ? null
                    : NumberUtils.sumIgnoringInfiniteNumbers(cyValues as number[], 0),
                py: pyValues.some((value) => value == null)
                    ? null
                    : NumberUtils.sumIgnoringInfiniteNumbers(pyValues as number[], 0),
            });
        }

        // Calculation rows run the same functions over the twin column (operands resolved above
        // or typed into free rows) — computed on the fly like everything else, never persisted.
        const rowValues: ISuperTableComponentRowFormValue[] = rowsFormArray.controls.map((control) => control.value);
        const calculationTwins = this.superTableHelper.computeFxCalculationRowValues(
            rowValues,
            this.calculations,
            (row, year) => this.fxOperandValueOf(row as ISuperTableComponentRowFormValue, year),
        );
        calculationTwins.forEach((twin, rowId) => this.fxTwinValues.set(rowId, twin));
    }

    /**
     * FX (REV-21512): a row's twin value as a calculation operand — user rows contribute what was
     * typed (non-numeric input is ignored, like the functional column ignores it), computed rows
     * contribute their derived twin, where null (missing rates) empties the calculation.
     */
    private fxOperandValueOf(row: ISuperTableComponentRowFormValue, year: 'cy' | 'py'): number | null | undefined {
        if (SuperTableHelper.isFxUserValueRowType(row.type)) {
            const raw = row[
                year === 'cy' ? SuperTableColumnType.CURRENT_YEAR_FX : SuperTableColumnType.PREVIOUS_YEAR_FX
            ] as number | string | null | undefined;
            const parsed = typeof raw === 'number' ? raw : raw == null || raw === '' ? NaN : +raw;

            return NumberUtils.isFinite(parsed) ? parsed : undefined;
        }
        if (row.type === SuperTableRowType.SPACE) {
            return undefined;
        }

        return this.fxTwinValues.get(row.rowId)?.[year] ?? null;
    }

    /** The stamped `__fx` values of the row's matched nodes. */
    private fxStampsOf(rowValue: ISuperTableComponentRowFormValue): IFxNodeStamp[] {
        if (this.balanceMappingItemsSelectionTree == null || this.incomeMappingItemsSelectionTree == null) {
            return [];
        }
        let mappingTree: MappingItemsSelectionNode;
        if (rowValue.financialType) {
            mappingTree =
                rowValue.financialType === TemplateType.BALANCE
                    ? this.balanceMappingItemsSelectionTree
                    : this.incomeMappingItemsSelectionTree;
        } else {
            mappingTree = {
                data: null,
                children: this.balanceMappingItemsSelectionTree.children.concat(this.incomeMappingItemsSelectionTree),
                type: ItemType.GROUP,
                financialType: null,
                expanded: true,
            };
        }
        const matchingNodes = this.superTableMapper.findMatchingNodesForRowItems(rowValue, mappingTree);

        return matchingNodes.map((node) => node.data?.__fx as IFxNodeStamp | undefined).filter((s): s is IFxNodeStamp => s != null);
    }

    /** Rate rows: closing/average from the context, position = display ÷ functional (03b formula). */
    private fxRateValuesOf(rowValue: ISuperTableComponentRowFormValue): { cy: number | null; py: number | null } {
        const ctx = this.fxNotesContext;
        if (ctx == null) {
            return { cy: null, py: null };
        }
        if (rowValue.rateSource === 'closing') {
            return { cy: ctx.closingRateCY, py: ctx.closingRatePY };
        }
        if (rowValue.rateSource === 'average') {
            return { cy: ctx.averageRateCY, py: ctx.averageRatePY };
        }
        const stamps = this.fxStampsOf(rowValue);
        if (stamps.length === EMPTY_ARRAY_LENGTH) {
            return { cy: null, py: null };
        }
        const functionalCY = NumberUtils.sumIgnoringInfiniteNumbers(stamps.map((s) => s.functionalCY), 0);
        const functionalPY = NumberUtils.sumIgnoringInfiniteNumbers(stamps.map((s) => s.functionalPY), 0);
        const displayCY = stamps.some((s) => s.displayCY == null)
            ? null
            : NumberUtils.sumIgnoringInfiniteNumbers(stamps.map((s) => s.displayCY as number), 0);
        const displayPY = stamps.some((s) => s.displayPY == null)
            ? null
            : NumberUtils.sumIgnoringInfiniteNumbers(stamps.map((s) => s.displayPY as number), 0);

        return {
            cy: displayCY == null || functionalCY === 0 ? null : displayCY / functionalCY,
            py: displayPY == null || functionalPY === 0 ? null : displayPY / functionalPY,
        };
    }

    /** Template helper: the twin/rate cell of a row-year; null renders as empty (rates as «–»). */
    fxCellOf(rowId: string | undefined, year: 'cy' | 'py'): number | null {
        if (rowId == null) {
            return null;
        }
        const values = this.fxTwinValues.get(rowId);

        return values == null ? null : values[year];
    }

    /**
     * Disabled-look controls so twin cells render through the SAME hamInput fields as the
     * functional cells. Deliberately outside `this.form` — computed values never persist (E2).
     */
    private readonly fxCellControls = new Map<string, UntypedFormControl>();

    fxCellControl(rowId: string | undefined, year: 'cy' | 'py'): UntypedFormControl {
        const key = `${rowId}|${year}`;
        const value = this.fxCellOf(rowId, year);
        let control = this.fxCellControls.get(key);
        if (control == null) {
            control = new UntypedFormControl(value);
            this.fxCellControls.set(key, control);
        } else if (control.value !== value) {
            control.setValue(value, { emitEvent: false });
        }

        return control;
    }

    getSegmentFlex(segment: ISuperTableSegment): number {
        let totalFlex = 0;
        segment.childColIds.forEach((columnId) => {
            const column =
                this.xAxisConfigCols.find((col) => col.columnId === columnId) ||
                columnId === SuperTableColumnType.CURRENT_YEAR ||
                columnId === SuperTableColumnType.PREVIOUS_YEAR;

            if (column) {
                totalFlex += 5;
            }
        });
        return totalFlex;
    }

    dropRow(event: CdkDragDrop<any[]>) {
        moveItemInArray(this.form.get('rows')['controls'], event.previousIndex, event.currentIndex);
        this.form.get('rows').updateValueAndValidity();
    }

    moveColumnLeft(columnIndextoMove: number, columnToMove: IXAxisConfigColumn): void {
        if (columnIndextoMove === FIRST_ELEMENT_INDEX) {
            throw new Error('cannot move column to the left');
        }

        const columnToSwitchWith: IXAxisConfigColumn = cloneDeep<IXAxisConfigColumn>(
            this.xAxisConfigCols[columnIndextoMove - 1],
        );

        // check if moved column belongs to a segment
        for (const segment of this.segments) {
            const colIndexToSwitchWithInSegment = segment.childColIds.findIndex(
                (colId) => colId === columnToSwitchWith.columnId,
            );

            if (segment.childColIds.includes(columnToMove.columnId)) {
                const colIndexInSegment = segment.childColIds.findIndex((colId) => colId === columnToMove.columnId);
                segment.childColIds[colIndexInSegment] = columnToSwitchWith.columnId;
            }

            if (segment.childColIds.includes(columnToSwitchWith.columnId) && colIndexToSwitchWithInSegment !== -1) {
                segment.childColIds[colIndexToSwitchWithInSegment] = columnToMove.columnId;
            }
        }

        this.prepareStartingColumnIdToSegmentMap();

        //switch columns

        const columnsCopy: IXAxisConfigColumn[] = cloneDeep<IXAxisConfigColumn[]>(this.xAxisConfigCols);

        let temp = columnsCopy[columnIndextoMove];
        columnsCopy[columnIndextoMove] = columnsCopy[columnIndextoMove - 1];
        columnsCopy[columnIndextoMove - 1] = temp;
        this.xAxisConfigCols = columnsCopy;

        this.handleFormValueChange();
    }

    moveColumnRight(columnIndextoMove: number, columnToMove: IXAxisConfigColumn): void {
        if (columnIndextoMove === this.xAxisConfigCols.length - 1) {
            throw new Error('cannot move column to the right');
        }

        const columnToSwitchWith: IXAxisConfigColumn = cloneDeep<IXAxisConfigColumn>(
            this.xAxisConfigCols[columnIndextoMove + 1],
        );

        // check if moved column belongs to a segment
        for (const segment of this.segments) {
            const colIndexToSwitchWithInSegment = segment.childColIds.findIndex(
                (colId) => colId === columnToSwitchWith.columnId,
            );

            if (segment.childColIds.includes(columnToMove.columnId)) {
                const colIndexInSegment = segment.childColIds.findIndex((colId) => colId === columnToMove.columnId);
                segment.childColIds[colIndexInSegment] = columnToSwitchWith.columnId;
            }

            if (segment.childColIds.includes(columnToSwitchWith.columnId) && colIndexToSwitchWithInSegment !== -1) {
                segment.childColIds[colIndexToSwitchWithInSegment] = columnToMove.columnId;
            }
        }

        this.prepareStartingColumnIdToSegmentMap();

        //switch columns

        const columnsCopy: IXAxisConfigColumn[] = cloneDeep<IXAxisConfigColumn[]>(this.xAxisConfigCols);

        let temp = columnsCopy[columnIndextoMove];
        columnsCopy[columnIndextoMove] = columnsCopy[columnIndextoMove + 1];
        columnsCopy[columnIndextoMove + 1] = temp;
        this.xAxisConfigCols = columnsCopy;

        this.handleFormValueChange();
    }

    editColumn(columnIndex: number, column: ISuperTableAddFreeColumnDialogResult): void {
        this.xAxisConfigCols[columnIndex] = {
            columnId: this.xAxisConfigCols[columnIndex].columnId,
            title: { [this.language]: column.title },
            type: column.dataType,
            belongingYear: column.belongingYear,
            format: this.xAxisConfigCols[columnIndex].format,
            columnWidth: column.columnWidth,
        };
        this.handleFormValueChange(false);
    }

    private handleCompareYears(): void {
        if (!this.compareYears) {
            const previousYearColumn = this.xAxisConfigCols.find(
                (col) => col.type === SuperTableColumnType.PREVIOUS_YEAR,
            );

            if (previousYearColumn) {
                this.handleColumnRemoval(previousYearColumn);
            }
            return;
        }

        if (!this.xAxisConfigCols.map((col) => col.type).includes(SuperTableColumnType.PREVIOUS_YEAR)) {
            const previousYearColumn: IXAxisConfigColumn = {
                columnId: SuperTableColumnType.PREVIOUS_YEAR,
                type: SuperTableColumnType.PREVIOUS_YEAR,
                belongingYear: SuperTableColumnBelongingYear.PREVIOUS_YEAR,
                format: {
                    styles: { [AumicoStyleOptions.RIGHT_ALIGNED]: { enabled: true, appliedAt: new Date() } } as Record<
                        AumicoStyleOptions,
                        { enabled: boolean; appliedAt: Date }
                    >,
                    numberOfDecimals: {
                        value: TWO_DECIMAL_PLACES,
                    },
                },
            };

            this.xAxisConfigCols.splice(1, 0, previousYearColumn);
            this.xAxisConfigCols = [...this.xAxisConfigCols];

            const rowsFormArray = this.form.get('rows') as UntypedFormArray;

            rowsFormArray.controls.forEach((control: FormGroup) => {
                control.addControl('previousValue', new UntypedFormControl(this.getPreviousValue(control.value)));
            });
        }
    }
    private handleHideCurrentYear(): void {
        if (this.hideCurrentYear) {
            const currentYearColumn = this.xAxisConfigCols.find(
                (col) => col.type === SuperTableColumnType.CURRENT_YEAR,
            );

            if (currentYearColumn) {
                this.handleColumnRemoval(currentYearColumn);
            }
            return;
        }

        if (!this.xAxisConfigCols.map((col) => col.type).includes(SuperTableColumnType.CURRENT_YEAR)) {
            const currentYearColumn: IXAxisConfigColumn = {
                columnId: SuperTableColumnType.CURRENT_YEAR,
                type: SuperTableColumnType.CURRENT_YEAR,
                belongingYear: SuperTableColumnBelongingYear.CURRENT_YEAR,
                format: {
                    styles: { [AumicoStyleOptions.RIGHT_ALIGNED]: { enabled: true, appliedAt: new Date() } } as Record<
                        AumicoStyleOptions,
                        { enabled: boolean; appliedAt: Date }
                    >,
                    numberOfDecimals: {
                        value: TWO_DECIMAL_PLACES,
                    },
                },
            };

            this.xAxisConfigCols.splice(1, 0, currentYearColumn);
            this.xAxisConfigCols = [...this.xAxisConfigCols];

            const rowsFormArray = this.form.get('rows') as UntypedFormArray;

            rowsFormArray.controls.forEach((control: FormGroup) => {
                control.addControl('currentValue', new UntypedFormControl(this.getCurrentValue(control.value)));
            });
        }
    }

    private getPreviousValue(row: ISuperTableComponentRowFormValue): number | undefined {
        if (row.itemsIds.length === EMPTY_ARRAY_LENGTH) {
            return undefined;
        }

        let mappingTree: MappingItemsSelectionNode;
        if (row.financialType) {
            mappingTree =
                row.financialType === TemplateType.BALANCE
                    ? this.balanceMappingItemsSelectionTree
                    : this.incomeMappingItemsSelectionTree;
        } else {
            mappingTree = {
                data: null,
                children: this.balanceMappingItemsSelectionTree.children.concat(this.incomeMappingItemsSelectionTree),
                type: ItemType.GROUP,
                financialType: null,
                expanded: true,
            };
        }

        const matchingNodes = this.superTableMapper.findMatchingNodesForRowItems(row, mappingTree);

        // REV-20899: previous-year row value includes the aggregated reclass.
        const itemsPreviousValues = matchingNodes.map((item) => this.finalPYValue(item.data));
        return NumberUtils.sumIgnoringInfiniteNumbers(itemsPreviousValues, 0);
    }

    private getCurrentValue(row: ISuperTableComponentRowFormValue): number | undefined {
        if (row.itemsIds.length === EMPTY_ARRAY_LENGTH) {
            return undefined;
        }

        let mappingTree: MappingItemsSelectionNode;
        if (row.financialType) {
            mappingTree =
                row.financialType === TemplateType.BALANCE
                    ? this.balanceMappingItemsSelectionTree
                    : this.incomeMappingItemsSelectionTree;
        } else {
            mappingTree = {
                data: null,
                children: this.balanceMappingItemsSelectionTree.children.concat(this.incomeMappingItemsSelectionTree),
                type: ItemType.GROUP,
                financialType: null,
                expanded: true,
            };
        }

        const matchingNodes = this.superTableMapper.findMatchingNodesForRowItems(row, mappingTree);

        // REV-20899: current-year row value includes the aggregated reclass.
        const itemsCurrentValues = matchingNodes.map((item) => this.finalCYValue(item.data));
        return NumberUtils.sumIgnoringInfiniteNumbers(itemsCurrentValues, 0);
    }

    private areIDsOrdered(allIDs: string[], someIDs: string[]) {
        // Create a map from ID to index
        const indexMap = new Map<string, number>();
        allIDs.forEach((id, index) => {
            indexMap.set(id, index);
        });

        // Convert IDs in someIDs to their indices in allIDs
        const indices = someIDs.map((id) => indexMap.get(id));

        // Check if these indices are consecutive
        for (let i = 1; i < indices.length; i++) {
            if (indices[i] !== indices[i - 1] + 1) {
                return false;
            }
        }

        return true;
    }

    private setLastGivenIds(): void {
        // Map the array of column IDs to an array of numbers by extracting the numeric part
        const filteredColumns = this.xAxisConfigCols
            .map((col) => col.columnId)
            .filter(
                (columnId) =>
                    columnId !== SuperTableColumnType.CURRENT_YEAR &&
                    columnId !== SuperTableColumnType.PREVIOUS_YEAR &&
                    // FX (REV-21512): twin ids are enum values too — parseInt would yield NaN here.
                    columnId !== SuperTableColumnType.CURRENT_YEAR_FX &&
                    columnId !== SuperTableColumnType.PREVIOUS_YEAR_FX,
            );

        if (filteredColumns.length === EMPTY_ARRAY_LENGTH) {
            this.lastGivenColumnIndex = -1;
        } else {
            const columnNumbers = filteredColumns.map((id) => parseInt(id.replace('col', ''), 10));
            // Find the maximum number in the array
            this.lastGivenColumnIndex = Math.max(...columnNumbers);
        }

        // Map the array of column IDs to an array of numbers by extracting the numeric part
        if (this.form.value.rows.length === EMPTY_ARRAY_LENGTH) {
            this.lastGivenRowIndex = -1;
            return;
        }
        const rowNumbers = this.form.value.rows
            .map((row) => row.rowId)

            .map((id) => parseInt(id.replace('row-', ''), 10));

        // Find the maximum number in the array
        this.lastGivenRowIndex = Math.max(...rowNumbers);
    }

    private shouldRecalculate(
        previousValue: ISuperTableComponentRowFormValue[],
        currentValue: ISuperTableComponentRowFormValue[],
    ): boolean {
        let recalculateRow = true;
        let recalculateColumn = true;
        let changedValuesCount = 0;
        // recalculate if changed value is not a part of a calculation colum
        currentValue.forEach((row: ISuperTableComponentRowFormValue, index: number) => {
            if (JSON.stringify(row) !== JSON.stringify(previousValue[index])) {
                changedValuesCount++;

                const columnCalculations = this.calculations.filter(
                    (calc) => calc.calcAxis === ISuperTableCalculationAxis.COLUMN,
                );

                for (const columnCalculation of columnCalculations) {
                    if (
                        previousValue[index] != null &&
                        row[columnCalculation.calcId] !== previousValue[index][columnCalculation.calcId]
                    ) {
                        recalculateColumn = false;
                    }
                }

                if (changedValuesCount === 1) {
                    // recalculate if changed value is not part of a calculation row
                    recalculateRow = !(row.type === SuperTableRowType.CALCULATION);
                }
            }
        });

        return recalculateRow && recalculateColumn;
    }

    onTitleChange(newTitle: string): void {
        this.form.get('tableTitle').patchValue(newTitle);
        this.handleFormValueChange();
    }

    toggleRowStylingMenu(rowIndex: number) {
        if (this.activeStyleMenuRow === rowIndex) {
            this.activeStyleMenuRow = null; // Close menu
        } else {
            // Close the flyout first
            this.closeRowContextFlyout();

            // Then open the styling menu after a brief delay to ensure flyout is closed
            setTimeout(() => {
                this.activeStyleMenuRow = rowIndex; // Open menu
                this.activeStyleMenuColumn = null; // Close column menu if open
            }, 100); // Increased delay to ensure flyout is fully closed
        }
    }
    toggleRowContextMenu(rowIndex: number, event?: MouseEvent) {
        if (this.isRowContextFlyoutOpen && this.activeContextMenuRow === rowIndex) {
            this.closeRowContextFlyout();
            return;
        }

        if (event) {
            // Calculate flyout position based on button click
            const buttonElement = event.target as HTMLElement;
            const rect = buttonElement.getBoundingClientRect();

            // Position flyout below and to the left of the button
            this.rowContextFlyoutPosition = {
                top: rect.bottom + 5,
                right: window.innerWidth - rect.right,
                left: rect.left - 300,
            };
        }

        this.activeContextMenuRow = rowIndex;
        this.isRowContextFlyoutOpen = true;

        // Close column flyout if open
        this.closeColumnContextFlyout();
    }

    toggleColumnStylingMenu(columnIndex: number) {
        if (this.activeStyleMenuColumn === columnIndex) {
            this.activeStyleMenuColumn = null; // Close menu
        } else {
            // Close the flyout first
            this.closeColumnContextFlyout();

            // Then open the styling menu after a brief delay to ensure flyout is closed
            setTimeout(() => {
                this.activeStyleMenuColumn = columnIndex; // Open menu
                this.activeStyleMenuRow = null; // Close row menu if open
            }, 100);
        }
    }
    toggleColumnContextMenu(columnIndex: number, event?: MouseEvent) {
        if (this.isColumnContextFlyoutOpen && this.activeContextMenuColumn === columnIndex) {
            this.closeColumnContextFlyout();
            return;
        }

        if (event) {
            // Calculate flyout position based on button click
            const buttonElement = event.target as HTMLElement;
            const rect = buttonElement.getBoundingClientRect();

            // Position flyout below and to the left of the button
            this.columnContextFlyoutPosition = {
                top: rect.bottom + 5,
                right: window.innerWidth - rect.right,
                left: rect.left - 300,
            };
        }

        this.activeContextMenuColumn = columnIndex;
        this.isColumnContextFlyoutOpen = true;

        // Close row flyout if open
        this.closeRowContextFlyout();
    }

    closeRowContextFlyout(): void {
        this.isRowContextFlyoutOpen = false;
        this.activeContextMenuRow = null;
    }

    closeColumnContextFlyout(): void {
        this.isColumnContextFlyoutOpen = false;
        this.activeContextMenuColumn = null;
    }

    applyRowStyle(rowIndex: number, style: AumicoStyleOptions) {
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        const row = rowsFormArray.at(rowIndex);

        if (row.get('format') == null) {
            (row as UntypedFormGroup).addControl('format', new FormControl());
        }

        const format = this.getFormatValue(row.value.format, style);
        row.get('format').patchValue(format);
    }

    private getFormatValue(existingFormat: ICellFormat, newStyle: AumicoStyleOptions): ICellFormat {
        const format: ICellFormat = {
            styles: {} as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>,
            numberOfDecimals: {
                value: TWO_DECIMAL_PLACES,
            },
        };

        // Map styles old structure(array) to new structure(map)
        if (existingFormat?.styles != null) {
            if (Array.isArray(existingFormat.styles)) {
                // Convert legacy array into timestamped map
                for (const oldStyle of existingFormat.styles) {
                    format.styles[oldStyle] = { enabled: true, appliedAt: new Date(0) }; // Legacy styles get low priority
                }
            } else {
                // Copy existing record
                Object.assign(format.styles, existingFormat.styles);
            }
        }

        // Map numberOfDecimals old structure(number) to new structure
        if (existingFormat?.numberOfDecimals != null) {
            if (typeof existingFormat.numberOfDecimals === 'number') {
                // Map number to {value: number}
                (format.numberOfDecimals as { value: number }).value = existingFormat.numberOfDecimals;
            } else {
                format.numberOfDecimals = { ...existingFormat.numberOfDecimals };
            }
        }

        if (
            newStyle === AumicoStyleOptions.DECREASE_DECIMALS &&
            (format.numberOfDecimals as { value: number }).value > MIN_DECIMAL
        ) {
            (format.numberOfDecimals as { value: number }).value -= 1;
            (format.numberOfDecimals as { value: number; appliedAt: Date }).appliedAt = new Date();
            return format;
        }

        if (
            newStyle === AumicoStyleOptions.INCREASE_DECIMALS &&
            (format.numberOfDecimals as { value: number }).value < MAX_DECIMAL
        ) {
            (format.numberOfDecimals as { value: number }).value += 1;
            (format.numberOfDecimals as { value: number; appliedAt: Date }).appliedAt = new Date();
            return format;
        }

        // If all an alignment style already existed, remove it
        const isAlignmentStyle = [
            AumicoStyleOptions.LEFT_ALIGNED,
            AumicoStyleOptions.CENTER_ALIGNED,
            AumicoStyleOptions.RIGHT_ALIGNED,
        ].includes(newStyle);

        if (isAlignmentStyle) {
            for (const alignStyle of [
                AumicoStyleOptions.LEFT_ALIGNED,
                AumicoStyleOptions.CENTER_ALIGNED,
                AumicoStyleOptions.RIGHT_ALIGNED,
            ]) {
                delete format.styles[alignStyle];
            }
        }

        // Toggle style
        const existingEntry = format.styles[newStyle];
        if (existingEntry?.enabled) {
            format.styles[newStyle] = { enabled: false, appliedAt: new Date() }; // mark as removed
        } else {
            format.styles[newStyle] = { enabled: true, appliedAt: new Date() }; // apply style
        }

        return format;
    }

    applyColumnStyle(columnIndex: number, style: AumicoStyleOptions) {
        let columnFormat = this.xAxisConfigCols[columnIndex].format;

        if (columnFormat == null) {
            columnFormat = {
                styles: {} as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>,
                numberOfDecimals: {
                    value: TWO_DECIMAL_PLACES,
                },
            };
        }

        this.xAxisConfigCols[columnIndex].format = { ...this.getFormatValue(columnFormat, style) };

        this.handleFormValueChange();
    }

    private mapOldFormatToNewFormat(oldFormat: ICellFormat): ICellFormat {
        const newFormat: ICellFormat = {
            styles: {} as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>,
            numberOfDecimals: {
                value: TWO_DECIMAL_PLACES,
            },
        };

        // Map styles old structure(array) to new structure(map)
        if (oldFormat?.styles != null) {
            if (Array.isArray(oldFormat.styles)) {
                // Convert legacy array into timestamped map
                for (const oldStyle of oldFormat.styles) {
                    newFormat.styles[oldStyle] = { enabled: true, appliedAt: new Date(0) }; // Legacy styles get low priority
                }
            } else {
                // Copy existing record
                Object.assign(newFormat.styles, oldFormat.styles);
            }
        }

        // Map numberOfDecimals old structure(number) to new structure
        if (oldFormat?.numberOfDecimals != null) {
            if (typeof oldFormat.numberOfDecimals === 'number') {
                // Map number to {value: number}
                (newFormat.numberOfDecimals as { value: number }).value = oldFormat.numberOfDecimals;
            } else {
                newFormat.numberOfDecimals = { ...oldFormat.numberOfDecimals };
            }
        }

        return newFormat;
    }

    resolveEffectiveCellStyles(rowFormat?: ICellFormat, colFormat?: ICellFormat): AumicoStyleOptions[] {
        return this.superTableHelper.resolveEffectiveCellStyles(rowFormat, colFormat);
    }

    resolveEffectiveDecimalPlaces(rowFormat?: ICellFormat, colFormat?: ICellFormat): number | undefined {
        return this.superTableHelper.resolveEffectiveDecimalPlaces(rowFormat, colFormat);
    }

    private setFormattingForColumns(): void {
        for (const column of this.xAxisConfigCols) {
            // Don't overwrite existing formatting if it is already saved
            if (column.format != null) {
                column.format = this.mapOldFormatToNewFormat(column.format);
                continue;
            }

            if (
                column.type === SuperTableColumnType.CALCULATION ||
                column.type === SuperTableColumnType.CURRENT_YEAR ||
                column.type === SuperTableColumnType.PREVIOUS_YEAR ||
                column.type === SuperTableColumnType.NUMBER
            ) {
                column.format = {
                    styles: {
                        [AumicoStyleOptions.RIGHT_ALIGNED]: { enabled: true, appliedAt: new Date() },
                    } as Record<AumicoStyleOptions, { enabled: boolean; appliedAt: Date }>,
                    numberOfDecimals: {
                        value: TWO_DECIMAL_PLACES,
                    },
                };
            }
        }
    }

    onReconcileClick(): void {
        this.isRowContextFlyoutOpen = false;
        this.reconcilePopUpOpened = true;
        this.allMappingItemsSelectionTree = {
            data: null,
            children: this.balanceMappingItemsSelectionTree.children.concat(this.incomeMappingItemsSelectionTree),
            type: ItemType.GROUP,
            financialType: null,
            expanded: true,
        };
    }

    onReconcileSelectedItemsChange(selectedItems: MappingItemsSelectionNode[]): void {
        this.selectedReconcileItemNodes = selectedItems;
    }

    onSaveReconcileSelection(): void {
        const rowsFormArray = this.form.get('rows') as UntypedFormArray;
        const row = rowsFormArray.at(this.activeContextMenuRow);

        if (row.get('reconcileItems') == null) {
            (row as UntypedFormGroup).addControl('reconcileItems', new FormControl());
        }

        if (this.selectedReconcileItemNodes == null || this.selectedReconcileItemNodes.length === EMPTY_ARRAY_LENGTH) {
            row.get('reconcileItems').setValue(null);
        } else {
            row.get('reconcileItems').setValue(
                this.selectedReconcileItemNodes.map((node) => ({
                    itemName: this.superTableMapper.getNodeIdentifier(node),
                    type: node.type,
                    financialtype: node.financialType,
                })),
            );
        }

        this.handleFormValueChange(true);

        this.onCancelReconcile();
    }

    onCancelReconcile(): void {
        this.closeRowContextFlyout();

        this.reconcilePopUpOpened = false;
    }

    private recalculateReconcilationMismatchResult(): void {
        if (this.balanceMappingItemsSelectionTree == null || this.incomeMappingItemsSelectionTree == null) {
            return;
        }

        const rowsFormArray = this.form.get('rows') as UntypedFormArray;

        for (const rowFormControl of rowsFormArray.controls) {
            const rowValue: ISuperTableComponentRowFormValue = rowFormControl.value;

            if (rowValue.reconcileItems == null || rowValue.reconcileItems.length === EMPTY_ARRAY_LENGTH) {
                continue;
            }

            this.allMappingItemsSelectionTree = {
                data: null,
                children: this.balanceMappingItemsSelectionTree.children.concat(this.incomeMappingItemsSelectionTree),
                type: ItemType.GROUP,
                financialType: null,
                expanded: true,
            };

            const matchingNodes = this.superTableMapper.findMatchingNodesForReconcileItems(
                rowFormControl.get('reconcileItems').value,
                this.allMappingItemsSelectionTree,
            );

            const reconcilationMisMatchResult = this.superTableHelper.getReconcilationMisMatchResult(
                matchingNodes,
                rowValue,
            );

            if (rowFormControl.get('reconcilationCurrentYearMismatch') == null) {
                (rowFormControl as UntypedFormGroup).addControl('reconcilationCurrentYearMismatch', new FormControl());
            }

            // If current year column is not hidden
            if (!this.hideCurrentYear) {
                rowFormControl
                    .get('reconcilationCurrentYearMismatch')
                    .setValue(reconcilationMisMatchResult.reconcilationCurrentYearMismatch);
            } else {
                rowFormControl.get('reconcilationCurrentYearMismatch').reset();
            }

            if (rowFormControl.get('reconcilationPreviousYearMismatch') == null) {
                (rowFormControl as UntypedFormGroup).addControl('reconcilationPreviousYearMismatch', new FormControl());
            }

            //If previous year column is not hidden
            if (this.compareYears) {
                rowFormControl
                    .get('reconcilationPreviousYearMismatch')
                    .setValue(reconcilationMisMatchResult.reconcilationPreviousYearMismatch);
            } else {
                rowFormControl.get('reconcilationPreviousYearMismatch').reset();
            }
        }
    }

    onDeleteColumn(): void {
        this.handleColumnRemoval(this.columnToRemove);
        this.columnToRemove = null;
        this.showColumnDeletionDialog = false;
    }

    onCancelDeleteColumn(): void {
        this.columnToRemove = null;
        this.showColumnDeletionDialog = false;
    }

    onColumnEdit(column: IXAxisConfigColumn, columnIndex: number): void {
        this.isColumnContextFlyoutOpen = false;
        if (column.type === SuperTableColumnType.CALCULATION) {
            this.editCalculationColumn(column, columnIndex);
            return;
        }

        this.showAddFreeColumnDialog = true;
        this.freeColumnFormValue = {
            title: column.title[this.language],
            dataType: column.type,
            belongingYear: column.belongingYear,
            columnWidth: column.columnWidth,
        };

        this.columnToEditIndex = columnIndex;

        this.freeColumnFormIsValid = true;
    }

    private editCalculationColumn(column: IXAxisConfigColumn, columnIndex: number): void {
        const matchingCalculation = this.calculations.find((calc) => calc.calcId === column.columnId);

        if (matchingCalculation == null) {
            throw new Error('calculation row not found');
        }

        this.currentSelectedCalculationFunction = matchingCalculation.function;
        this.currentSelectedCalculation = cloneDeep<ISuperTableCalculation>(matchingCalculation);

        this.onCalculationFunctionSelection(this.currentSelectedCalculationFunction);
    }

    deleteSegment(segmentToDelete: ISuperTableSegment): void {
        const segmentToDeleteIndex = this.segments.findIndex((seg) => seg.segmentId === segmentToDelete.segmentId);
        this.segments.splice(segmentToDeleteIndex, 1);
        this.prepareStartingColumnIdToSegmentMap();
    }

    // Listen for clicks on the entire document
    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        if (this.disableOutsideClick) return;
        if (this.activeStyleMenuColumn == null && this.activeStyleMenuRow == null) {
            return;
        }

        // Check if the click is inside the menu or the trigger
        const clickedInsideMenu = this.menuContainerRef?.nativeElement?.contains(event.target);
        const clickedOnTrigger = this.menuTriggerRef?.nativeElement?.contains(event.target);

        // If the click is outside both, close the menu
        if (
            !clickedInsideMenu &&
            !clickedOnTrigger &&
            (this.activeStyleMenuColumn != null || this.activeStyleMenuRow != null || this.activeContextMenuRow != null)
        ) {
            this.activeStyleMenuColumn = null;
            this.activeStyleMenuRow = null;
        }
    }

    private setCalculationInvalidRef(calcId: string): void {
        // Add invalidRef flag to the calculation
        const calculation = this.calculations.find(calc => calc.calcId === calcId);
        if (calculation) {
            calculation.invalidRef = true;
        }
    }

    private clearCalculationInvalidRef(calcId: string): void {
        // Remove invalidRef flag from the calculation
        const calculation = this.calculations.find(calc => calc.calcId === calcId);
        if (calculation) {
            calculation.invalidRef = false;
        }
    }


    /// Drag and Drop Methods ///

    onRowDragMoved(event: any) {
        this.lastDragEvent = event;
        const edgeThreshold = 60; // px from top/bottom to start scrolling
        const scrollSpeed = 20; // px per interval
        const pointerY = event.pointerPosition.y;
        // Find the scrollable parent of the table
        const tableElement = (event.source && event.source.element) ? event.source.element.nativeElement : null;
        let scrollContainer = null;
        if (tableElement) {
            scrollContainer = this.findScrollableParent(tableElement);
        }
        if (!scrollContainer) {
            scrollContainer = document.getElementById('mainscrollcontent');
        }
        if (!scrollContainer) return;
        const rect = scrollContainer.getBoundingClientRect();

        // Scroll up
        if (pointerY < rect.top + edgeThreshold) {
            this.startAutoScroll(-scrollSpeed, scrollContainer, event);
        }
        // Scroll down
        else if (pointerY > rect.bottom - edgeThreshold) {
            this.startAutoScroll(scrollSpeed, scrollContainer, event);
        } else {
            this.stopAutoScroll();
        }
    }

    startAutoScroll(delta: number, scrollContainer: HTMLElement, dragEvent?: any) {
        if (this.autoScrollInterval) return;
        this.autoScrollInterval = setInterval(() => {
            scrollContainer.scrollTop += delta;
            // Simulate mousemove event at current pointer position
            if (dragEvent && dragEvent.pointerPosition) {
                const evt = new MouseEvent('mousemove', {
                    clientX: dragEvent.pointerPosition.x,
                    clientY: dragEvent.pointerPosition.y,
                    bubbles: true
                });
                document.dispatchEvent(evt);
            }
        }, 16); // ~60fps
    }

    stopAutoScroll() {
        if (this.autoScrollInterval) {
            clearInterval(this.autoScrollInterval);
            this.autoScrollInterval = null;
        }
    }

    onRowDragEnded() {
        this.stopAutoScroll();
        this.lastDragEvent = null;
    }

    // Utility to find scrollable parent
    private findScrollableParent(element: HTMLElement): HTMLElement | null {
        let parent = element.parentElement;
        while (parent) {
            if (this.isScrollable(parent)) {
                return parent;
            }
            parent = parent.parentElement;
        }
        return null;
    }

    private isScrollable(element: HTMLElement): boolean {
        const overflowY = window.getComputedStyle(element).overflowY;
        return (overflowY === 'scroll' || overflowY === 'auto') && element.scrollHeight > element.clientHeight;
    }
}
